import type { Clock } from './core/clock';
import type { Rng } from './core/rng';
import { GameLoop, type FrameSource } from './core/loop';
import {
  GameStateStore,
  initialGameState,
  type Phase,
  type PublicGameState,
} from './state/gamestate';
import type { PlatformCapabilities } from './platform';
import { Machine, initialState, type MachineState } from './state/machine';
import {
  Shell,
  button,
  el,
  headline,
  meta,
  numberField,
  prompt,
  row,
  text,
  textArea,
  textField,
  type CardSpec,
  type ChromeSpec,
  type Notice,
} from './ui/shell';
import { thumbZoneGuard } from './ui/thumbzone';
import { FxLayer } from './fx/canvas';
import { KeyboardInsetWatcher, accentBar, keyAction } from './input/keyboard';
import { ItemLibrary } from './store/items';
import { ensureSchemaVersion, type Store } from './store/store';
import {
  KEY,
  PREFIX,
  type Attempt,
  type ItemRecord,
  MANUAL_COMPONENTS,
  type ManualComponent,
  type ManualEntry,
  type SessionRecord,
} from './store/schema';
import { RunSession, gradeDraw, type DrawOutcome } from './state/session';
import { runId as makeRunId } from './core/ids';
import { toDrawRecord } from './round/quota';
import { buildRound, type RoundDraw } from './round/builder';
import {
  baselineFieldRange,
  loadProfile,
  markDemoSeen,
  markReviewRead,
  saveIntakeCap,
  needsSetup,
  parseBaselineField,
  saveBaseline,
  weekNumber,
  type BaselineField,
  type LearnerProfile,
} from './store/profile';
import { SERVED, conversionPattern, conversionRule, seedItem } from './content';
import {
  balance as balanceOf,
  componentStatus,
  grammarShareMetric,
  perTierAccuracy,
  productiveVocabulary,
  retention as retentionOf,
  sessionMetrics,
  DEFAULT_WINDOW_DAYS,
  GRAMMAR_WINDOW_DAYS,
} from './tracker/metrics';
import { baselineLines, defaultView, detailView, type Figure } from './tracker/tablero';
import { review28, reviewDue, type Review28 } from './tracker/review28';
import {
  CHECKPOINTS,
  diagnose,
  noveltySeries,
  weekOf,
  weekSixVerdict,
  type Diagnosis,
  type Verdict,
  type WeekPoint,
} from './tracker/novelty';
import { exportCsv, exportFilename, exportJson } from './tracker/export';
import {
  COMPONENT_LABELS,
  MINUTE_CHIPS,
  emptyDraft,
  logReceipt,
  logSession,
  parseMinutes,
  takesNote,
  type LogDraft,
} from './tracker/manuallog';
import {
  markVetted,
  vettingPage,
  vettingProgress,
  type VettingPage,
} from './tracker/vetting';
import type { ComponentStatus } from './tracker/metrics';

/** Everything El Tablero renders, built once when it is open (T66). */
interface TrackerFigures {
  /** The three numbers (SPEC §8.2). */
  summary: Figure[];
  /** Everything else, one tap away. */
  detail: Figure[];
  baseline: { label: string; value: string }[];
  /** The four components the game cannot observe, and whether each has been logged (DoD-9). */
  components: ComponentStatus[];
  /** Items awaiting Diana. The way in to the review screen (T76). */
  unvetted: number;
  /** Weeks 1-12, plotted without a trend line (T73). */
  weeks: WeekPoint[];
  /** The game's own kill criteria, from week 6 (T73). */
  verdict: Verdict;
  /** The four-week review, when one is due (T72). */
  review: Review28 | null;
  /** The checkpoint calendar and the three diagnoses, in order (T74). */
  checkpoints: { label: string; dueIso: string; passed: boolean }[];
  diagnoses: Diagnosis[];
}
import { PALETTE, TIER_COLOUR } from './ui/a11y';
import { present, promptSegments, type ItemExtras, type Presentation } from './modes/types';
import {
  DEMO_ITEM_ID,
  DEMO_RULE_ID,
  demoDue,
  demostracionView,
  type DemostracionView,
} from './modes/demostracion';
import { feedbackView } from './modes/feedback';
import { SPEECH_TROUBLE, segmentalFeedback } from './modes/speech';
import type { Recognizer } from './speech/recognizer';
import { Speaker } from './speech/synth';
import type { SegmentalNote } from './lang/phonemes';
import {
  needsFirstExposure,
  teachingView,
  withIntroduction,
  type TeachingView,
} from './modes/teaching';
import {
  revealView,
  ruleReach,
  rulesProvenIn,
  type RevealRule,
  type RevealView,
} from './state/reveal';
import {
  encodingPrompt,
  isUsableEncoding,
  requiresEncoding,
  withEncoding,
} from './modes/encoding';
import {
  assembled,
  placeTile,
  remaining,
  removeTile,
  tileGroup,
  undoTile,
  type Placement,
} from './modes/tiles';
import { summarise } from './scheduler/queue';
import { NEW_CAP_PER_DAY, introductionTimes, planIntake } from './scheduler/intake';
import { baseUnlockState, evaluateGates, toUnlockMap, type UnlockMap } from './scheduler/gating';
import { intakeCandidates } from './round/builder';

/**
 * The application: boot sequence, run timer, and the card for each phase.
 *
 * What lives here is only what has to see everything at once — the machine, the projection, the
 * shell, the library, and the loop. Everything with a rule in it lives one module down and is pure:
 * the machine owns sequence, the scheduler owns intervals and gating, the round builder owns
 * composition, the validator owns grading. This file wires them together and renders the result.
 *
 * It holds no policy of its own. Any `if` here that decides something pedagogical is in the wrong
 * file, because it would be a rule that no unit test can reach.
 *
 * Modes, grading, and per-item persistence arrive from T47 onward; the run card is currently a
 * frame with a timer in it.
 */

/**
 * Hand a file to the person reading the page (T75).
 *
 * An object URL and a synthetic click. There is no server to POST to and there never will be — the
 * core loop is offline by contract (SPEC §10) — so this is the only mechanism available. The URL is
 * revoked afterwards, because an unrevoked blob URL pins its contents in memory for the life of the
 * document, and the whole item corpus is not a small blob.
 */
function download(filename: string, body: string, type: string): void {
  const url = URL.createObjectURL(new Blob([body], { type: `${type};charset=utf-8` }));
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';
  document.body.append(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

/** The tier's hue, or the one accent where a card belongs to no tier. Decorative (D-185). */
function hueFor(tier: number | null): string {
  if (tier === null) return PALETTE.accent;
  return TIER_COLOUR[tier as 1 | 2 | 3 | 4 | 5 | 6] ?? PALETTE.accent;
}

/**
 * The tier ladder's names, as the learner meets them (SPEC §5).
 *
 * Spanish, because they are what the tiers are called, and because "Núcleo" on a card is a small
 * reminder that the thing being learned is a language and not a syllabus.
 */
const TIER_NAME: Readonly<Record<number, string>> = {
  1: 'Sonido',
  2: 'Núcleo',
  3: 'Motor',
  4: 'Conversión',
  5: 'Frase',
  6: 'Tiempo',
};

/** Default ronda length. 90 seconds (BRIEF <game>). */
export const DEFAULT_ROUND_MS = 90_000;

/** The event that reaches the entry card from each phase that can reach it (SPEC §3). */
const ENTRY_EVENT: Partial<
  Record<Phase, 'boot-complete' | 'setup-complete' | 'reveal-dismiss' | 'tracker-close'>
> = {
  boot: 'boot-complete',
  setup: 'setup-complete',
  reveal: 'reveal-dismiss',
  tracker: 'tracker-close',
};

export interface AppDeps {
  clock: Clock;
  rng: Rng;
  frames: FrameSource;
  root: HTMLElement;
  /** Persistence. Injected so tests can run the whole app against `MemoryStore`. */
  store: Store;
  capabilities: PlatformCapabilities;
  buildId: string;
  roundMs?: number;
  /**
   * The effects canvas (T58). Optional: the game is complete without it.
   *
   * Nothing the learner needs is drawn there, so a build with no canvas, a canvas that fails to get
   * a context, or one covered entirely plays identically. That is the test of whether an effects
   * layer has stayed an effects layer.
   */
  fxCanvas?: HTMLCanvasElement | null;
  /** No motion at all (`prefers-reduced-motion: reduce`). */
  reducedMotion?: boolean;
  /**
   * The microphone, or null where speech cannot work here (T60).
   *
   * Null in a browser with no `SpeechRecognition`, and — the case that matters — in an installed
   * iOS web app, where the API exists and never returns a result. Every speech card degrades to a
   * typed one, silently, because the alternative is a mic button that does nothing.
   */
  recognizer?: Recognizer | null;
  /**
   * The device's voice (T64). Optional: audio is never load-bearing.
   *
   * Usage scenario 1 is a dark room with the sound off, so every card must be answerable in silence.
   * A missing speaker removes a control and changes nothing else.
   */
  speaker?: Speaker | null;
  /**
   * Throw when a card violates the thumb zone (T44, DoD-17) instead of reporting it.
   *
   * Chosen by the composition root: on in development and under the harness, off in a production
   * build. See DECISIONS D-089.
   */
  strictLayout?: boolean;
}

export interface App {
  loop: GameLoop;
  state: GameStateStore;
  start(): void;
  stop(): void;
  /** Enter the run. No-op unless we are on the entry card. */
  startRun(): void;
  /** End the run and bank. Always safe: every completed result is already persisted. */
  endRun(): void;
  toEntry(): void;
  /** Reset to a freshly-booted state, store wiped. Used by `__harness.reset()`. */
  reset(): Promise<void>;
  /** Mark the conversion demonstration seen without playing it. Used by `__harness.skipDemo()`. */
  skipDemo(): Promise<void>;
  /** The state machine, so the harness can assert no illegal transition was attempted (T45). */
  machine: Machine;
  /** Resolves once the store is loaded and the entry card is up. Awaited by the harness. */
  ready: Promise<void>;
}

export function createApp(deps: AppDeps): App {
  /*
   * Kept for the harness and for tests that want a run of a stated length; the app itself no longer
   * ends a run on it (D-263). Nothing reads it except the escalation curve, when one is supplied.
   */
  const roundMs = deps.roundMs;
  const projection = new GameStateStore(initialGameState(deps.buildId, deps.rng.seed));
  // The thumb zone is enforced once, here, for every card the shell will ever mount (T44).
  const shell = new Shell(deps.root, {
    afterRender: thumbZoneGuard({ strict: deps.strictLayout ?? false }),
  });
  /**
   * The single source of truth for where we are (T45, SPEC §3).
   *
   * `PublicGameState.phase` is a *mirror* of this, republished on every render. Nothing reads the
   * phase back out of the published projection to decide anything — that projection exists for
   * tests to assert on, and a game that steers by its own telemetry has two sources of truth and
   * therefore none.
   */
  const machine = new Machine(initialState());
  machine.subscribe(mirror);
  /*
   * The effects layer. Given the seeded stream so the same seed paints the same background, and told
   * nothing about correctness — `bank()` takes a position and no verdict, and never will.
   */
  const fx = new FxLayer(deps.fxCanvas ?? null, {
    rng: deps.rng,
    ...(deps.reducedMotion === undefined ? {} : { reducedMotion: deps.reducedMotion }),
    pixelRatio: typeof window === 'undefined' ? 1 : window.devicePixelRatio || 1,
  });
  let runCounter = 0;
  let runStartedAt = 0;
  /** Null until the store has loaded. The boot card is what is on screen until then. */
  let library: ItemLibrary | null = null;
  /**
   * The attempt log, in memory.
   *
   * Loaded once at boot and appended to as the run banks attempts — never re-read from disk mid-run.
   * Three separate things derive from it (tier gating, intake's rolling window, and the tracker's
   * metrics), and re-querying for each would make them answerable at slightly different instants.
   */
  let attempts: Attempt[] = [];
  /** Rolling-accuracy tier gates (invariant 4). Recomputed from the attempt log, never from time. */
  let unlocked: UnlockMap = baseUnlockState();

  /** New items this run would introduce. Shown on the entry card beside the review count. */
  let newItemsAvailable = 0;
  /** First launch and the pre-game baseline (T46b). Null until the store has loaded. */
  let profile: LearnerProfile | null = null;
  /**
   * What is typed into the setup card, before it is parsed.
   *
   * Held as raw strings outside the card, not as parsed numbers, and deliberately not part of the
   * card's repaint key. Keying on it would rebuild the card on every keystroke — which dismisses
   * the iOS keyboard mid-number — and parsing on every keystroke would mean deciding "9" is a valid
   * answer while the learner is still typing "90".
   */
  const setupDraft: Record<BaselineField, string> = { retentionPct: '', dailyMinutes: '' };
  /** Why the last setup submission was refused, if it was. Rendered as the card's notice. */
  let setupError: string | null = null;
  /**
   * Fixed steps elapsed in the current run. The remaining time is derived from this rather than
   * decremented, because repeatedly subtracting the non-representable 1000/60 accumulates visible
   * drift over a 90-second round.
   */
  let runSteps = 0;
  /** The run in progress: its plan, its cursor, and its counters. Null outside a run. */
  let session: RunSession | null = null;
  /** The draw on screen. Null between items. */
  let activeDraw: RoundDraw | null = null;
  /**
   * What the active draw's mode decided to show (T49).
   *
   * Built once when the draw is presented rather than on every frame: the render loop runs at 60Hz
   * and a presentation is the one thing on screen that cannot change while the card is up. Rebuilding
   * it per frame would be sixty identical cloze cuts a second, and would invite a mode to start
   * depending on when it was asked.
   */
  let activePresentation: Presentation | null = null;
  /**
   * Which tiles have been placed, in the order they were placed (T50).
   *
   * Run state, held here rather than inside the mode: a mode describes what is asked, and a half-
   * built answer is exactly what a reload is supposed to lose (DoD-5). Cleared with every draw.
   */
  let placedTiles: Placement = [];
  /** The first-exposure card's content, or null when no item is being introduced (T59c). */
  let activeTeaching: TeachingView | null = null;
  /** True while the microphone is open on the current card (T61). */
  let listening = false;
  /**
   * The device's voice, and whether it is the wrong variety (T64).
   *
   * Read once at boot rather than per card: `voiceschanged` keeps the `Speaker` current, and the
   * notice is a fact about the device that does not change between two cards.
   */
  const voice = deps.speaker ?? null;
  /**
   * Why the last utterance produced nothing, if it did.
   *
   * Rendered as a line on the card and cleared by the next attempt. **Never banked**: nothing was
   * produced, so there is nothing to grade, and marking a noisy room as a wrong answer would let it
   * lower a retention figure.
   */
  let speechTrouble: string | null = null;
  /**
   * The reveal card's content, built once when the ronda closes (T57).
   *
   * Not on every render: it walks the whole library to name the items it lists, and the render loop
   * runs at 60Hz. It is also a statement about a moment — what the schedule looked like when the
   * ronda ended — so recomputing it while the card is on screen would let it drift.
   */
  let lastReveal: RevealView | null = null;
  /**
   * Whether the tracker is showing its drilldown (SPEC §8.2).
   *
   * Three numbers by default; everything else is one tap away, and the tap is remembered only for
   * as long as the card is open — a tracker that reopened on the detail view would have quietly
   * made the detail the default.
   */
  let trackerDetail = false;
  /** Which page of the Diana gate is open (T76). Reset each time the screen is entered. */
  let vettingIndex = 0;
  /**
   * What is being typed into the log screen (T68).
   *
   * Outside the card's repaint key, like every other draft in this app: keying on it would rebuild
   * the card on each keystroke and dismiss the iOS keyboard mid-number.
   */
  let logDraft: LogDraft = emptyDraft();
  /** Why the last log submission was refused, or what it produced. Rendered as one line. */
  let logMessage: string | null = null;
  /** How many files the last export produced. Rendered as a receipt, then forgotten. */
  let exportedCount = 0;
  /** Session records, for the tracker's Balance and session figures. Loaded at boot. */
  let sessions: SessionRecord[] = [];
  /** Manual log entries, for Balance and the "not logged" lines. Loaded at boot. */
  let manualEntries: ManualEntry[] = [];
  /**
   * Where focus should land after the next tile rebuild, by test id (T50).
   *
   * Null on a fresh draw, so the card focuses its primary action like every other card. Set only by
   * a tile action, because only a tile action rebuilds the card the learner is mid-answer on.
   */
  let tileFocus: string | null = null;
  /** When the active card became answerable, for `latencyMs`. From the injected clock. */
  let presentedAt = 0;
  /** What is typed into the answer field. Outside the card key, so typing never rebuilds it. */
  let answerDraft = '';
  /** What is typed into the demonstration field, before it is submitted. */
  let demoDraft = '';
  /** What was typed into the demonstration, once submitted. Null while it is still being asked. */
  let demoTyped: string | null = null;
  /** The reveal was taken on the demonstration instead of an answer. */
  let demoRevealed = false;
  /** The graded, banked outcome the feedback card is showing. */
  let lastOutcome: DrawOutcome | null = null;
  /**
   * Exactly what was submitted for that outcome.
   *
   * Captured at submission rather than read back off the draft, because "no sé" clears the draft
   * before submitting and the tile line is cleared with the next draw. The feedback card shows it
   * back beside the correct form, and it has to be the string that was actually graded.
   */
  let lastAnswer = '';
  /** Whether the active draw is a second sighting of its item in this run (invariant 1). */
  let activeIsRepeat = false;
  /**
   * The scaffold on this card has been revealed (T54, invariant 2, SPEC §6.4).
   *
   * One way only: nothing un-reveals it. The learner who has seen the hint has seen it, and a card
   * that let him take it back would let him launder a helped answer into one that moves an interval.
   */
  let activeHinted = false;
  /**
   * What is typed into the encoding card (T55). Outside the card key, so typing never rebuilds it.
   *
   * Never graded and never an `Attempt`: this is a note the learner writes about his own life, and
   * there is no right version of it.
   */
  let encodingDraft = '';

  /*
   * `firstLaunchAt` is deliberately absent here: it is a persisted fact read from the profile at
   * boot, not the current time. Stamping it at construction would reset week 1 on every launch and
   * silently flatten the novelty panel that exists to detect decay (RESEARCH §4).
   */
  projection.patchTelemetry({
    speechAvailable: deps.capabilities.speechAvailable,
    standalone: deps.capabilities.standalone,
  });

  /** Mirror the machine onto the published projection. The machine decides; this only reports. */
  function mirror(state: MachineState): void {
    projection.patch({
      phase: state.phase,
      runPhase: state.runPhase,
      ready: state.phase !== 'boot',
    });
  }

  /**
   * The boot sequence (SPEC §3): load the store, compute the queue, then open the entry card.
   *
   * Asynchronous, and the machine sits in `boot` until it finishes — which is why `boot` is a phase
   * at all rather than an instant. The loop is already running and painting the boot card, so a
   * cold IndexedDB open shows "Loading" rather than a blank screen.
   */
  async function bootUp(): Promise<void> {
    await ensureSchemaVersion(deps.store);
    const now = deps.clock.now();
    // Before anything else: this is the launch `firstLaunchAt` names, and it is only correct if it
    // is written now, whatever the learner does next.
    profile = await loadProfile(deps.store, now);
    projection.patchTelemetry({
      firstLaunchAt: profile.firstLaunchAt,
      weekNumber: weekNumber(profile.firstLaunchAt, now),
    });

    const loaded = await ItemLibrary.load(deps.store, SERVED, now);
    library = loaded.library;
    attempts = (await deps.store.list<Attempt>(PREFIX.attempt)).map((entry) => entry.value);
    sessions = (await deps.store.list<SessionRecord>(PREFIX.session)).map((entry) => entry.value);
    manualEntries = (await deps.store.list<ManualEntry>(PREFIX.manual)).map((entry) => entry.value);
    unlocked = toUnlockMap(evaluateGates(baseUnlockState(), attempts));
    refreshQueue();

    // The baseline is asked for once, before the first run, because by week 6 the pre-game numbers
    // are unrecoverable and asking then would produce an answer contaminated by the game itself.
    if (needsSetup(profile)) machine.dispatch({ type: 'setup-required' });
    else toEntry();
  }

  /**
   * Record the baseline and open the entry card (T46b).
   *
   * The write is awaited *before* the transition, for the same reason `bank` is the only way out of
   * `grading`: a card that advanced first would show the entry screen while the one number the
   * week-6 verdict depends on was still in flight, and a force-quit at that instant would lose it
   * with no second chance to ask.
   *
   * A refused field stops the whole submission rather than saving the other one. Half a baseline
   * saved under "asked once, never re-prompted" is a fact the learner can never correct here.
   */
  async function submitSetup(): Promise<void> {
    if (!profile || machine.get().phase !== 'setup') return;

    const parsed = {
      retentionPct: parseBaselineField(setupDraft.retentionPct, 'retentionPct'),
      dailyMinutes: parseBaselineField(setupDraft.dailyMinutes, 'dailyMinutes'),
    };
    const refused = Object.values(parsed).find((result) => !result.ok);
    if (refused && !refused.ok) {
      setupError = refused.reason;
      render();
      return;
    }
    setupError = null;

    profile = await saveBaseline(
      deps.store,
      profile,
      {
        retentionPct: parsed.retentionPct.ok ? parsed.retentionPct.value : null,
        dailyMinutes: parsed.dailyMinutes.ok ? parsed.dailyMinutes.value : null,
      },
      deps.clock.now(),
    );
    toEntry();
    render();
  }

  /**
   * Recompute the queue figures the entry card and telemetry report.
   *
   * The depth is the honest number, however unwelcome (invariant 7). Nothing here caps it, and the
   * entry card renders whatever comes back.
   */
  function refreshQueue(): void {
    if (!library) return;
    const now = deps.clock.now();
    const due = library.due(now);
    const summary = summarise(due, now);

    /*
     * The entry card also needs the intake preview, not just the review count. On a fresh install
     * every item is `new`, so the due queue is empty and a card reporting only depth would say
     * "Nothing due" and then start a run that introduces ten words — technically true and
     * practically a lie. The number shown is the one intake will actually admit, cap and
     * suppression included (invariant 6).
     */
    const intake = planIntake({
      dueQueue: due,
      candidates: intakeCandidates(library.all(), unlocked, deps.capabilities),
      introducedAt: introductionTimes(attempts),
      now,
    });

    newItemsAvailable = intake.newItems.length;
    projection.patch({ queueDepth: summary.depth });
    projection.patchTelemetry({
      queueDepth: summary.depth,
      estimatedQueueMinutes: summary.estimatedMinutes,
      intakeSuppressed: intake.suppressed,
    });
  }

  /**
   * Return to the entry card from wherever the entry card is reachable (SPEC §3).
   *
   * The event is chosen from the current phase rather than found by trying each in turn. Probing by
   * dispatch would work, but every failed probe is a recorded illegal transition — and the point of
   * that log is that it stays empty, so filling it with the app's own guesses would make it useless
   * as a signal.
   */
  /**
   * Build the tracker's figures (T66).
   *
   * Only while it is open: it walks the whole attempt log and the whole library, and the render loop
   * runs at 60Hz. Every window it uses is a named constant from `tracker/metrics`, so no view here
   * can invent one below the seven-day floor (DoD-8).
   */
  function trackerFigures(): TrackerFigures {
    const now = deps.clock.now();
    const firstLaunchAt = profile?.firstLaunchAt ?? now;
    return {
      summary: defaultView({
        retention: retentionOf(attempts, now),
        balance: balanceOf(manualEntries, sessions, now),
        grammar: grammarShareMetric(attempts, now),
        retentionWindowDays: DEFAULT_WINDOW_DAYS,
        balanceWindowDays: DEFAULT_WINDOW_DAYS,
        grammarWindowDays: GRAMMAR_WINDOW_DAYS,
      }),
      detail: detailView({
        perTierAccuracy: perTierAccuracy(attempts, now),
        sessions: sessionMetrics(sessions, now),
        productiveVocabulary: productiveVocabulary(library?.all() ?? []),
        windowDays: DEFAULT_WINDOW_DAYS,
      }),
      baseline: baselineLines(profile?.baseline ?? { retentionPct: null, dailyMinutes: null }),
      components: MANUAL_COMPONENTS.map((component) =>
        componentStatus(manualEntries, component, now),
      ),
      unvetted: library?.all().filter((item) => !item.vetted).length ?? 0,
      weeks: noveltySeries({ attempts, sessions, firstLaunchAt, now }),
      verdict: weekSixVerdict({
        retention: retentionOf(attempts, now).rate,
        medianDailyMinutes: sessionMetrics(sessions, now).medianMinutes,
        /*
         * Structurally 1 in this app, and computed rather than hard-coded so the claim stays
         * auditable: there is no recognition mode anywhere (invariant 3), so if this ever came back
         * below 1 something would have gone badly wrong and the verdict should say so.
         */
        productionShare: attempts.length === 0 ? null : 1,
        baseline: profile?.baseline ?? { retentionPct: null, dailyMinutes: null },
        week: weekOf(firstLaunchAt, now),
      }),
      review: reviewDue(firstLaunchAt, profile?.lastReview28At ?? null, now)
        ? review28({
            retention: retentionOf(attempts, now),
            grammar: grammarShareMetric(attempts, now),
            sessions: sessionMetrics(sessions, now),
            manual: manualEntries,
            productiveVocabulary: productiveVocabulary(library?.all() ?? []),
            currentIntakeCap: profile?.newCapPerDay ?? NEW_CAP_PER_DAY,
            now,
          })
        : null,
      checkpoints: CHECKPOINTS.map((checkpoint) => {
        const dueAt = firstLaunchAt + checkpoint.months * 30 * 86_400_000;
        return { label: checkpoint.label, dueIso: new Date(dueAt).toISOString().slice(0, 10), passed: now >= dueAt };
      }),
      diagnoses: diagnose({
        balanceRatio: balanceOf(manualEntries, sessions, now).ratio,
        conversationsLogged: manualEntries.filter((entry) => entry.component === 'conversation').length,
        retention: retentionOf(attempts, now).rate,
        daysWithoutReviews:
          DEFAULT_WINDOW_DAYS - sessionMetrics(sessions, now).daysActive,
        windowDays: DEFAULT_WINDOW_DAYS,
        manualMinutes: manualEntries.reduce((total, entry) => total + entry.minutes, 0),
        measuredMinutes: sessions.reduce((total, item) => total + item.appMeasuredMinutes, 0),
      }),
    };
  }

  /**
   * The page of items awaiting a human (T76).
   *
   * Carrier sentences come from the corpus by id, the same lookup every mode uses (D-112): the
   * register of a phrase often only shows in the sentence it lives in, so a reviewer needs it.
   */
  function vettingFigures(): VettingPage {
    const all = library?.all() ?? [];
    const examples = new Map<string, string>();
    for (const item of all) {
      const example = seedItem(item.id)?.example?.es;
      if (example) examples.set(item.id, example);
    }
    return vettingPage({ items: all, pageIndex: vettingIndex, examples });
  }

  /**
   * Confirm every item on this page (T76).
   *
   * Awaited before the next page is shown: the flip is the entire product of the review, and a page
   * that advanced before its write settled would let a force-quit lose work that is tedious to redo.
   * The page index deliberately does **not** advance — approving removes these items from the
   * pending list, so the same index is already the next six.
   */
  async function approveVettingPage(): Promise<void> {
    if (!library || machine.get().phase !== 'vetting') return;
    const page = vettingFigures();
    const flipped = markVetted(library.all(), page.rows.map((row) => row.id));
    for (const record of flipped) await library.put(record);
    render();
  }

  /**
   * Record one manual entry, and whatever it generates (T68, T69).
   *
   * Both writes are awaited before the screen reports anything: a conversation error is the most
   * valuable item in the app — a mistake made in front of a proficient speaker — and losing one to a
   * force-quit between the entry and the item would lose the only record that it happened.
   */
  async function submitLog(): Promise<void> {
    if (!library || machine.get().phase !== 'log') return;
    const minutes = parseMinutes(logDraft.minutes);
    if (!minutes.ok) {
      logMessage = minutes.reason;
      render();
      return;
    }

    const at = deps.clock.now();
    const result = logSession({
      draft: logDraft,
      minutes: minutes.minutes,
      at,
      id: `${logDraft.component}-${at.toString(36)}`,
    });

    // Items first, entry second — the entry names them in `derivedItemIds`, and an entry pointing at
    // items that do not exist is worse than items nothing points at.
    for (const candidate of result.candidates) await library.put(candidate);
    await deps.store.set(KEY.manual(result.entry.id), result.entry);

    manualEntries.push(result.entry);
    logMessage = logReceipt(result);
    logDraft = emptyDraft(logDraft.component);
    refreshQueue();
    render();
  }

  /**
   * One-tap export (T75, DoD-13).
   *
   * Four files: the whole log as JSON, and three CSV tables. Written through an object URL and a
   * synthetic click, which is the only way a page can hand a file to the person reading it without
   * a server — and there is no server here by design (SPEC §10).
   */
  async function exportLog(): Promise<void> {
    const now = deps.clock.now();
    const payload = {
      attempts,
      manual: manualEntries,
      sessions,
      items: library?.all() ?? [],
      telemetry: projection.get().telemetry,
      exportedAt: now,
    };

    const files: { name: string; body: string; type: string }[] = [
      {
        name: `${exportFilename('log', now)}.json`,
        body: exportJson(payload),
        type: 'application/json',
      },
      ...exportCsv(payload).map((table) => ({
        name: `${exportFilename(table.name, now)}.csv`,
        body: table.csv,
        type: 'text/csv',
      })),
    ];

    for (const file of files) download(file.name, file.body, file.type);
    exportedCount = files.length;
    render();
  }

  /** Open El Tablero. Always on the three numbers, never on the drilldown. */
  function openTracker(): void {
    if (!machine.dispatch({ type: 'tracker-open' })) return;
    trackerDetail = false;
    render();
  }

  function toEntry(): void {
    const event = ENTRY_EVENT[machine.get().phase];
    if (event) machine.dispatch({ type: event });
    projection.patch({ tier: null, activeItemId: null, mode: null, roundTimerMs: 0 });
  }

  /**
   * Compose the run and draw its first item (SPEC §6.2).
   *
   * The whole plan is built up front from one seeded stream, so the same seed and the same store
   * replay the same run (DoD-14). It is not *shown* up front: the learner sees one card at a time
   * and never what is next, which is the mystery half of the gamification evidence (RESEARCH §3).
   */
  /**
   * Is there anything at all to do right now?
   *
   * Both halves are normal, healthy states on their own. Nothing due means the scheduler has nothing
   * at risk; no new items today means intake has done its ten (invariant 6). Together they mean the
   * learner is finished until something falls due, and the honest response is to say so rather than
   * to open a run with nothing in it.
   */
  function nothingToDraw(): boolean {
    return projection.get().queueDepth === 0 && newItemsAvailable === 0;
  }

  /**
   * The demonstration's view, or null when it is not on screen.
   *
   * Read out of the corpus rather than written here: the rule's name, its note and the word are
   * content, and a second copy in the app would be a second thing to keep true.
   */
  function buildDemo(): DemostracionView | null {
    if (machine.get().phase !== 'demo') return null;
    const rule = conversionRule(DEMO_RULE_ID);
    const item = seedItem(DEMO_ITEM_ID);
    if (!rule || !item) return null;
    return demostracionView({
      ruleName: rule.name,
      ruleNote: rule.note,
      en: item.en,
      es: item.es,
      typed: demoTyped,
      revealed: demoRevealed,
    });
  }

  /**
   * Leave the demonstration for the run it delayed.
   *
   * The profile is written **before** the transition, so a tab closed here has still seen it. The
   * card can only be honest once — the second time, the word is no longer one he was never taught.
   */
  /**
   * Mark the demonstration seen without showing it — the harness's way past it.
   *
   * Writes the same field the real path writes, so the app cannot tell the difference and there is
   * no second code path to keep true. See `Harness.skipDemo` for why this exists at all.
   */
  async function skipDemo(): Promise<void> {
    if (profile) profile = await markDemoSeen(deps.store, profile, deps.clock.now());
    render();
  }

  async function leaveDemo(): Promise<void> {
    if (profile) profile = await markDemoSeen(deps.store, profile, deps.clock.now());
    if (!machine.dispatch({ type: 'demo-done' })) return;
    beginRun();
  }

  function startRun(): void {
    if (!library) return;
    /*
     * Nothing to draw is not a run (D-273).
     *
     * Entering one anyway produced the loop reported from a real session: the plan is empty, the
     * first `drawNext` finds no draw, the run ends immediately, the reveal reports nothing, and the
     * entry card comes back with the same button that started it. Before the run timer was removed
     * an empty plan idled instead, so the dead end was a card that sat there rather than a cycle.
     * Neither is right; the fix is not to start.
     */
    if (nothingToDraw()) return;
    /*
     * The demonstration, once, before the first run ever begins (D-252).
     *
     * Here rather than inside the round builder because it is not a draw: nothing is scheduled,
     * scored, or counted by it. The run it delays starts by exactly the same path afterwards.
     */
    if (profile && demoDue(profile)) {
      if (machine.dispatch({ type: 'demo-start' })) {
        demoTyped = null;
        demoRevealed = false;
        render();
      }
      return;
    }
    if (!machine.dispatch({ type: 'run-start' })) return;
    beginRun();
  }

  /**
   * Everything a run needs once the machine is already in `run`.
   *
   * Split out because there are two ways in — `run-start` from the entry card, and `demo-done` from
   * the demonstration — and the two must set up an identical run. A demonstration that produced a
   * subtly different first ronda would make its own point dishonestly.
   */
  function beginRun(): void {
    if (!library) return;
    runCounter++;
    runStartedAt = deps.clock.now();
    runSteps = 0;
    answerDraft = '';
    lastOutcome = null;
    lastReveal = null;
    projection.patch({ roundTimerMs: 0, runSummary: null });

    const plan = buildRound({
      items: library.all(),
      now: runStartedAt,
      unlocked,
      capabilities: deps.capabilities,
      introducedAt: introductionTimes(attempts),
      // The persisted seven-day window, so the 25% cap does not restart from an empty ledger each
      // run — a cap that forgets is not a cap (invariant 9).
      history: attempts.map(toDrawRecord),
      rng: deps.rng.fork(`run-${runCounter}`),
      ...(roundMs === undefined ? {} : { runMs: roundMs }),
    });

    session = new RunSession(makeRunId(deps.rng.seed, runCounter), runStartedAt, plan.draws);
    drawNext();
  }

  /**
   * Present the next draw, or end the run if the plan is spent.
   *
   * A spent plan used to idle, because the ninety-second timer owned when a run was over. With the
   * timer gone (D-263) nothing else would ever end it, and a card that says "nothing left" and then
   * sits there is not a resting state, it is a dead end.
   *
   * Note what "spent" means: the plan holds the **whole** due queue, never a prefix of it — reviews
   * are never truncated (invariant 7) — plus the day's intake. So reaching the end of it is reaching
   * the end of the work, which is the only honest reason to stop.
   */
  function drawNext(): void {
    const draw = session?.next() ?? null;
    activeDraw = draw;
    activePresentation = draw
      ? present(draw.mode, {
          item: draw.item,
          extras: extrasFor(draw.item),
          // Forked per draw and labelled by run and index, so the same seed lays the same tiles out
          // twice and a scripted run replays exactly (DoD-14).
          rng: deps.rng.fork(`present-${runCounter}-${draw.index}`),
        })
      : null;
    placedTiles = [];
    tileFocus = null;
    activeHinted = false;
    encodingDraft = '';
    answerDraft = '';
    lastAnswer = '';
    lastOutcome = null;

    if (!draw) {
      projection.patch({ tier: null, activeItemId: null, mode: null });
      // `exhausted` first so the machine leaves `drawing` by a legal edge; then the run closes and
      // the reveal reports what the ronda did.
      machine.dispatch({ type: 'exhausted' });
      endRun();
      return;
    }

    projection.patch({ tier: draw.tier, activeItemId: draw.item.id, mode: draw.mode });

    /*
     * A word he has never been asked for is shown before it is asked for (T59c).
     *
     * Not a scaffold and not something he can request: it is a property of the draw, and the machine
     * has no edge into `teaching` except from `drawing`. Unscored — nothing is banked here.
     */
    if (needsFirstExposure(draw.item) && machine.dispatch({ type: 'teach' })) {
      activeTeaching = teachingView(draw.item, extrasFor(draw.item));
      render();
      /*
       * Say it, once, as the card arrives (T82).
       *
       * Asked for directly: the pronunciation should be *part of* meeting the word, not a thing to
       * go and fetch. Spoken after `render()` so the word is on screen before it is heard — the
       * point is the pairing, and sound arriving ahead of the letters is just a noise.
       *
       * Nothing depends on it working. `Speaker.say` is a no-op where there is no voice, iOS may
       * refuse the first utterance of a page that has had no gesture, and either way the card is
       * complete without it: the buttons below repeat it on demand.
       */
      voice?.say(draw.item.es);
      return;
    }
    presentDraw(draw);
  }

  /** Put the drawn card up and start its clock. Reached directly, or from the first-exposure card. */
  function presentDraw(draw: RoundDraw): void {
    activeTeaching = null;
    machine.dispatch({ type: 'present' });
    // Asked before the draw is recorded, not after: once `present` has run the item is seen by
    // definition, and every attempt would look like a repeat.
    activeIsRepeat = session?.hasSeen(draw.item.id) ?? false;
    session?.present(draw);
    machine.dispatch({ type: 'ready' });
    presentedAt = deps.clock.now();
  }

  /**
   * Grade the answer, **persist it**, and only then show feedback (SPEC §10, DoD-5).
   *
   * The two writes are awaited before `bank` is dispatched. That ordering is the whole of DoD-5: a
   * tab closed between the answer and the feedback must lose nothing, so by the time anything is on
   * screen about this item, the attempt and the item's new memory are already on disk.
   *
   * An empty answer is "no sé" — a real, graded failure, not a cancel.
   */
  async function submitAnswer(): Promise<void> {
    if (!machine.acceptsAnswer() || !activeDraw || !session || !library) return;
    const draw = activeDraw;
    if (!machine.dispatch({ type: 'submit' })) return;

    const now = deps.clock.now();
    lastAnswer = currentAnswer();
    const outcome = gradeDraw({
      draw,
      answer: lastAnswer,
      // What the card on screen actually asked for. The mode decides that; nothing here re-derives it.
      expected: activePresentation?.expected ?? draw.item.es,
      // ...and what it takes to name the error, from the same place.
      ...(activePresentation?.context ? { context: activePresentation.context } : {}),
      runId: session.runId,
      now,
      latencyMs: Math.max(0, now - presentedAt),
      // The whole of invariant 2 passes through this one field: `gradeDraw` hands it to the gate,
      // the gate refuses to advance the interval, and the attempt records that it did (DoD-1).
      hinted: activeHinted,
      isRepeatWithinRun: activeIsRepeat,
    });

    // Item first, attempt second. If the tab dies between them the attempt is missing and the
    // memory has moved, which under-counts one review; the other order would double-count it on a
    // replay, and a phantom review is worse than a forgotten one.
    await library.put(outcome.record);
    await deps.store.set(KEY.attempt(outcome.attempt.id), outcome.attempt);

    attempts.push(outcome.attempt);
    unlocked = toUnlockMap(evaluateGates(baseUnlockState(), attempts));
    session.bank(outcome, now);
    lastOutcome = outcome;

    machine.dispatch({ type: 'bank' });
    // The same ripple whether the answer was right or wrong. See `FxLayer.bank`.
    fx.bank();
    refreshQueue();
    render();
  }

  /**
   * The answer as it currently stands, whichever way the card takes one.
   *
   * One function, so that "what was submitted" and "what the card shows" cannot drift apart. An
   * empty string here is "no sé" — a real graded failure, not a cancel.
   */
  function currentAnswer(): string {
    const shown = activePresentation;
    if (shown?.input === 'tiles' && shown.tiles) return assembled(shown.tiles, placedTiles);
    return answerDraft;
  }

  /**
   * The next tile to place, or the submit button once the bank is empty.
   *
   * "Once the bank is empty" is the only moment this card offers the primary action to a keyboard,
   * and that is the right moment: every word is down, so submitting is the only thing left to do.
   */
  function nextBankFocus(): string | null {
    const shown = activePresentation;
    if (shown?.input !== 'tiles' || !shown.tiles) return null;
    const next = remaining(shown.tiles, placedTiles)[0];
    return next === undefined ? 'answer-submit' : `tile-bank-${next}`;
  }

  /**
   * Open the microphone for this card (T61).
   *
   * The transcript is submitted the moment it arrives — a spoken answer is committed by having been
   * said, and a "was that right?" confirmation step would turn production into recognition. A
   * failure banks nothing and leaves the card answerable, by voice or by typing.
   */
  function listen(): void {
    const recognizer = deps.recognizer;
    if (!recognizer || listening || !machine.acceptsAnswer()) return;
    listening = true;
    speechTrouble = null;

    /*
     * `start()` before `render()`, and the order is load-bearing on iOS.
     *
     * Rendering rebuilds the card, and `listening` is in the card key — so the button that received
     * the tap is removed from the document and replaced. Safari ties a page's transient activation
     * to that gesture, and starting the engine *after* tearing the element out asks for the
     * microphone from a handler whose element no longer exists. Started first, the request happens
     * while the tap is unambiguously still in progress.
     *
     * `start()` is synchronous and its callbacks are not, so nothing is missed by rendering second.
     */
    recognizer.start({
      onResult(result) {
        listening = false;
        answerDraft = result.transcript;
        void submitAnswer();
      },
      onError(failure) {
        listening = false;
        // An abort is the learner letting go, not a problem to report at him.
        speechTrouble = failure === 'aborted' ? null : SPEECH_TROUBLE[failure];
        render();
      },
      onEnd() {
        listening = false;
        render();
      },
    });
    render();
  }

  /** Stop listening and take whatever was heard. */
  function stopListening(): void {
    if (listening) deps.recognizer?.stop();
  }

  /**
   * Done reading the new item; now be asked for it (T59c).
   *
   * Nothing is banked and nothing is graded. The only thing that happens is that the card he was
   * shown becomes the card he is asked for.
   */
  async function finishTeaching(): Promise<void> {
    const draw = activeDraw;
    if (machine.get().runPhase !== 'teaching' || !draw || !library) return;

    /*
     * The introduction is written down before the question is asked.
     *
     * Recorded on *dismissal* rather than on display: a learner who force-quits while reading has
     * not finished reading, and being shown the word again is the right outcome. The draw's own copy
     * of the record is replaced too — grading writes the item back afterwards, and a stale copy
     * would silently drop the field that was just set.
     */
    const introduced = withIntroduction(draw.item, deps.clock.now());
    await library.put(introduced);
    activeDraw = { ...draw, item: introduced };

    presentDraw(activeDraw);
    render();
  }

  /**
   * Dismiss feedback and move on — to the encoding step where one is owed, otherwise to the next
   * draw (invariant 5, SPEC §3).
   *
   * The debt is read from the *banked* record, so it survives a run that ended mid-step: the
   * `failureCount` is already on disk and the step is owed the next time the item is failed.
   *
   * It is collected on a failure and never on a correct answer. An item carrying the debt that is
   * then produced correctly is not made to write about a word it has just retrieved — that reads as
   * a punishment for getting it right, and the association exists for an item that is still winning.
   */
  function continueRun(): void {
    const outcome = lastOutcome;
    const record = outcome?.record;
    if (
      outcome &&
      record &&
      !outcome.grade.correct &&
      requiresEncoding(record) &&
      machine.dispatch({ type: 'encode' })
    ) {
      encodingDraft = '';
      render();
      return;
    }
    if (!machine.dispatch({ type: 'next' })) return;
    // No clock to consult any more (D-263). `drawNext` ends the run when the plan is spent, which is
    // now the only thing that ends one.
    drawNext();
  }

  /**
   * Store the line and go back to drawing (T55).
   *
   * Awaited before the transition, like every other write in this loop: the encoding step is forced
   * exactly once, so a line lost to a force-quit between the save and the next card is one the
   * learner is never asked for again.
   */
  async function submitEncoding(): Promise<void> {
    const record = lastOutcome?.record;
    if (!library || !record || machine.get().runPhase !== 'encoding') return;
    // Not "invalid": there is no right version. An empty box is simply not a written line, and the
    // card stays up rather than reporting an error at someone who is holding a baby.
    if (!isUsableEncoding(encodingDraft)) return;

    const updated = withEncoding(record, encodingDraft);
    await library.put(updated);
    lastOutcome = { ...lastOutcome!, record: updated };
    encodingDraft = '';

    if (!machine.dispatch({ type: 'encoded' })) return;
    drawNext();
    render();
  }

  function endRun(): void {
    const endedAt = deps.clock.now();
    if (!machine.dispatch({ type: 'run-end' })) return;
    // The session is what Balance and the session figures are drawn from, so a ronda that is not
    // recorded is a ronda the tracker will later report as never having happened.
    if (session) {
      const record: SessionRecord = {
        runId: session.runId,
        startedAt: session.startedAt,
        endedAt,
        appMeasuredMinutes: Math.max(0, (endedAt - session.startedAt) / 60_000),
        itemsAttempted: session.summary(endedAt).itemsAttempted,
        itemsBanked: session.summary(endedAt).itemsBanked,
      };
      sessions.push(record);
      void deps.store.set(KEY.session(record.runId), record);
    }
    const summary = session
      ? { ...session.summary(endedAt), rulesUnlocked: provenRules(session.runId) }
      : null;
    lastReveal =
      summary && library
        ? revealView({
            summary,
            items: new Map(library.all().map((record) => [record.id, record])),
            rules: revealRules(summary.rulesUnlocked),
            now: endedAt,
          })
        : null;
    activeDraw = null;
    activePresentation = null;
    placedTiles = [];
    tileFocus = null;
    activeHinted = false;
    encodingDraft = '';
    lastOutcome = null;
    answerDraft = '';
    projection.patch({
      roundTimerMs: 0,
      tier: null,
      activeItemId: null,
      mode: null,
      // Built from what happened, not from the plan: a run ended after four of twenty draws
      // reports four.
      runSummary: summary ?? {
            runId: makeRunId(deps.rng.seed, runCounter),
            startedAt: runStartedAt,
            endedAt,
            itemsAttempted: 0,
            itemsBanked: 0,
            promoted: [],
            returningTomorrow: [],
            rulesUnlocked: [],
            newItemsIntroduced: [],
          },
    });
    session = null;
  }

  /**
   * Conversion rules the learner proved for the first time in this run (T57).
   *
   * Measured, like everything else: a rule counts once one of its words has been produced correctly
   * and unaided. Computed here rather than inside `RunSession` because it needs the whole attempt
   * log — "first ever" is not a fact about one run.
   */
  function provenRules(runId: string): string[] {
    return rulesProvenIn(runId, attempts, (itemId) => seedItem(itemId)?.rule);
  }

  /** Those rules, resolved to the two lines the reveal card shows for each. */
  function revealRules(ids: readonly string[]): RevealRule[] {
    const out: RevealRule[] = [];
    for (const id of ids) {
      const rule = conversionRule(id);
      // Only an ending rule generates anything. A false-friend set has no rule behind it, so
      // claiming reach for it would be the one thing this card must not do (D-075).
      if (!rule || rule.kind !== 'ending') continue;
      out.push({ id, rule: rule.name, reach: ruleReach(rule.from, rule.hazards.length) });
    }
    return out;
  }

  /**
   * Back to a freshly-booted entry card, with the persisted store wiped.
   *
   * Only `__harness.reset()` reaches this. There is no path to it from the UI, and there must not
   * be: an in-app "clear my data" button next to a review queue is a way to lose months of
   * scheduling to one mis-tap in the dark.
   */
  async function reset(): Promise<void> {
    runCounter = 0;
    runStartedAt = 0;
    runSteps = 0;
    session = null;
    activeDraw = null;
    activePresentation = null;
    placedTiles = [];
    tileFocus = null;
    activeHinted = false;
    activeTeaching = null;
    activeIsRepeat = false;
    encodingDraft = '';
    lastOutcome = null;
    lastReveal = null;
    answerDraft = '';
    shell.invalidate();
    machine.reset();
    library = null;
    // The wipe includes the profile, so the reset app is a genuine first launch and comes back up
    // on the setup card. A reset that quietly kept the baseline would make the one path through
    // that card untestable.
    profile = null;
    setupDraft.retentionPct = '';
    setupDraft.dailyMinutes = '';
    setupError = null;
    projection.patch({ ...initialGameState(deps.buildId, deps.rng.seed) });
    projection.patchTelemetry({
      speechAvailable: deps.capabilities.speechAvailable,
      standalone: deps.capabilities.standalone,
    });
    await deps.store.clear();
    await bootUp();
  }

  /** When the entry card last recomputed what is due, on the injected clock. See `update`. */
  let queueCheckedAt = 0;

  function update(stepMs: number): void {
    /*
     * The entry card re-reads the queue as time passes.
     *
     * `refreshQueue` used to run only after a bank and at boot, which was survivable while the start
     * button always worked: whatever the card said, a run could be opened. Now that a finished day
     * withdraws the button (D-273), a stale queue is a dead end — the app sits on "Nothing due"
     * through the moment something falls due, and there is no way back in.
     *
     * That is not only a test artifact. A tab left open overnight is the ordinary case: this game is
     * opened at 3:40 a.m. and put down, and the next thing it is asked is whether there is anything
     * to do.
     *
     * Once a second of *clock* time, and only on the entry card. Compared against the clock rather
     * than accumulated from `stepMs` because the two differ exactly when it matters: `setNow` moves
     * the clock a day and pumps one frame, which an accumulator never notices, and "a day passed
     * while the card was up" is the case this exists for.
     */
    if (machine.get().phase === 'entry') {
      const now = deps.clock.now();
      if (Math.abs(now - queueCheckedAt) >= 1_000) {
        queueCheckedAt = now;
        const before = projection.get().queueDepth;
        const beforeNew = newItemsAvailable;
        refreshQueue();
        if (projection.get().queueDepth !== before || newItemsAvailable !== beforeNew) render();
      }
    }

    if (machine.isRunning()) {
      runSteps++;
      /*
       * Elapsed, not remaining, and nothing ends because of it (D-263).
       *
       * `roundTimerMs` stays on the projection because BRIEF <architecture> asks for a round timer
       * there, and because the session record needs the run's length either way. What changed is
       * its direction and its authority: it counts up, it is not displayed, and it closes nothing.
       */
      projection.patch({ roundTimerMs: Math.round(runSteps * stepMs) });
    }
    // The effects simulation advances on the fixed step, never in render, so it runs at the same
    // rate whatever the frame rate is — and so a background tab that stops painting does not
    // accumulate a minute of drift and then apply it in one jump.
    fx.update(stepMs);
    // Gamepad polling will be added here in T77 — inside the fixed update, never in render.
  }

  function render(): void {
    const s = projection.get();
    const view: View = {
      newItemsAvailable,
      setupDraft,
      setupError,
      draw: activeDraw,
      presentation: activePresentation,
      answer: answerDraft,
      answered: lastAnswer,
      teaching: activeTeaching,
      tracker: s.phase === 'tracker' ? trackerFigures() : null,
      trackerDetail,
      vetting: s.phase === 'vetting' ? vettingFigures() : null,
      logDraft,
      logMessage,
      exportedCount,
      voiceNotice: voice?.current().notice ?? null,
      /*
       * A **Spanish** voice, not merely a synthesiser.
       *
       * `available()` answers "is there a speech synthesiser", which is a different question. A
       * device with only an English voice would read `perro` in English — and every control that
       * offers to model a pronunciation is worse than useless if what it models is the wrong
       * language. Silence is the honest answer there.
       */
      canHear: (voice?.current().voice ?? null) !== null,
      listening,
      speechTrouble,
      reveal: lastReveal,
      hinted: activeHinted,
      encoding: encodingDraft,
      placed: placedTiles,
      tileFocus,
      outcome: lastOutcome,
      demo: buildDemo(),
      demoDraft,
    };
    const actions: Actions = {
      startRun,
      endRun,
      toEntry,
      continueRun,
      editDemo(value) {
        demoDraft = value;
      },
      submitDemo() {
        // Graded for the feedback line and banked nowhere: no attempt, no interval, no share of
        // the grammar window. See `src/modes/demostracion.ts`.
        demoTyped = demoDraft;
        render();
      },
      revealDemo() {
        demoRevealed = true;
        render();
      },
      finishDemo() {
        void leaveDemo();
      },
      editSetup(field, value) {
        setupDraft[field] = value;
      },
      submitSetup() {
        void submitSetup();
      },
      editAnswer(value) {
        answerDraft = value;
      },
      // Resolved at tap time, not at build time: the card is rebuilt whenever its key changes, so a
      // reference captured when the bar was made would type into a field that is no longer mounted.
      answerField: () => deps.root.querySelector<HTMLInputElement>('[data-testid="answer-input"]'),
      demoField: () => deps.root.querySelector<HTMLInputElement>('[data-testid="demo-input"]'),
      encodingField: () =>
        deps.root.querySelector<HTMLInputElement>('[data-testid="encoding-input"]'),
      submitAnswer() {
        void submitAnswer();
      },
      submitUnknown() {
        answerDraft = '';
        placedTiles = [];
        void submitAnswer();
      },
      /**
       * Reveal the scaffold (SPEC §6.4).
       *
       * The attempt is marked hinted *here*, at the reveal, and not at submission: the cost is for
       * having been shown the answer's shape, and it is owed whether or not the learner goes on to
       * use it. Grading, feedback and persistence are unchanged — a hinted attempt is a real result
       * that simply does not satisfy the item's due date (invariants 1 and 2).
       */
      editEncoding(value) {
        encodingDraft = value;
      },
      submitEncoding() {
        void submitEncoding();
      },
      finishTeaching() {
        void finishTeaching();
      },
      openTracker,
      /**
       * Accept the four-week review's recommendation (T72, DoD-10).
       *
       * Reached only by an explicit tap on the offer. Nothing calls this on its own.
       */
      applyIntakeAdvice() {
        void (async () => {
          const figures = trackerFigures();
          if (!profile || !figures.review || figures.review.offer === null) return;
          profile = await saveIntakeCap(
            deps.store,
            profile,
            figures.review.proposedIntakeCap,
            deps.clock.now(),
          );
          refreshQueue();
          render();
        })();
      },
      /** Read and dismiss the four-week review. The next one is 28 days from now. */
      dismissReview() {
        void (async () => {
          if (!profile) return;
          profile = await markReviewRead(deps.store, profile, deps.clock.now());
          render();
        })();
      },
      exportLog() {
        void exportLog();
      },
      openLog() {
        if (!machine.dispatch({ type: 'log-open' })) return;
        logDraft = emptyDraft();
        logMessage = null;
        render();
      },
      closeLog() {
        if (machine.dispatch({ type: 'log-close' })) render();
      },
      chooseComponent(component) {
        logDraft = { ...logDraft, component };
        logMessage = null;
        render();
      },
      editLogMinutes(value) {
        logDraft.minutes = value;
      },
      setLogMinutes(value) {
        logDraft = { ...logDraft, minutes: String(value) };
        logMessage = null;
        render();
      },
      editLogNote(value) {
        logDraft.note = value;
      },
      submitLog() {
        void submitLog();
      },
      openVetting() {
        if (!machine.dispatch({ type: 'vetting-open' })) return;
        vettingIndex = 0;
        render();
      },
      closeVetting() {
        if (machine.dispatch({ type: 'vetting-close' })) render();
      },
      approveVettingPage() {
        void approveVettingPage();
      },
      skipVettingPage() {
        // Left `vetted: false` and returned to later. The absence of a human's confirmation is the
        // safe state, so skipping costs nothing and deletes nothing.
        vettingIndex += 1;
        render();
      },
      closeTracker() {
        if (machine.dispatch({ type: 'tracker-close' })) render();
      },
      toggleTrackerDetail() {
        trackerDetail = !trackerDetail;
        render();
      },
      hear(text) {
        voice?.say(text);
      },
      listen,
      stopListening,
      revealScaffold() {
        if (!machine.acceptsAnswer() || activeHinted) return;
        activeHinted = true;
        render();
      },
      /*
       * Each of these names where focus goes next, because each rebuilds the card under the
       * learner's hands. Placing a word leaves focus on the next word to place; taking one back
       * leaves it on the tile that just returned to the bank. Without that, focus falls to the
       * primary action and the next Enter submits a half-built sentence.
       */
      placeTile(index) {
        placedTiles = placeTile(placedTiles, index);
        tileFocus = nextBankFocus();
        render();
      },
      removeTile(index) {
        placedTiles = removeTile(placedTiles, index);
        tileFocus = `tile-bank-${index}`;
        render();
      },
      undoTile() {
        const last = placedTiles[placedTiles.length - 1];
        placedTiles = undoTile(placedTiles);
        tileFocus = last === undefined ? null : `tile-bank-${last}`;
        render();
      },
    };
    const card = cardFor(s, deps.capabilities, view, actions);
    shell.render([card], s.phase, chromeFor(s));
    // The background belongs to the tier being practised. Colour only: the layer still knows nothing
    // about what is being asked or how it went (D-158).
    fx.setColour(hueFor(card.tier ?? null));
    fx.resize(deps.root.clientWidth, deps.root.clientHeight);
    fx.render();
    projection.publish();
  }

  const loop = new GameLoop({ clock: deps.clock, frames: deps.frames, update, render });

  /**
   * Keyboard is a first-class input path, not an accessibility afterthought: usage scenario 3 is
   * twenty minutes at a PC, and it is the only input the headless harness can drive natively.
   */
  function onKeyDown(event: KeyboardEvent): void {
    const state = machine.get();
    const active = document.activeElement;
    /*
     * What the key means is `input/keyboard.ts`'s decision, not this listener's. Everything read
     * here is a fact about the world — the machine's state and what has focus — and everything
     * decided from those facts is in a pure function a unit test can exhaust.
     */
    const action = keyAction(event.key, {
      phase: state.phase,
      runPhase: state.runPhase,
      // Asked of the machine, never inferred from which element happens to have focus.
      acceptsAnswer: machine.acceptsAnswer(),
      inField: active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement,
      onButton: active instanceof HTMLButtonElement,
    });
    if (!action) return;

    event.preventDefault();
    switch (action) {
      case 'start-run':
        return startRun();
      case 'submit-setup':
        return void submitSetup();
      case 'submit-answer':
        return void submitAnswer();
      case 'submit-encoding':
        return void submitEncoding();
      case 'finish-teaching':
        return void finishTeaching();
      case 'continue':
        return continueRun();
      case 'to-entry':
        return toEntry();
      case 'end-run':
        // Always safe — every completed result is already banked (DoD-5).
        return endRun();
    }
  }

  /**
   * Where the software keyboard is, published for the stylesheet (T48).
   *
   * Started with the app rather than with the run card, because the setup card has fields too and
   * the reading has to be current before anything needs to dodge a keyboard that is already up —
   * a learner who returns to the tab mid-answer does not get a resize event to tell us.
   */
  const keyboardInset = new KeyboardInsetWatcher(window);

  // Kicked off by `start()`; held here so the harness can await the entry card rather than poll.
  let ready: Promise<void> = Promise.resolve();

  return {
    loop,
    state: projection,
    get ready() {
      return ready;
    },
    start(): void {
      document.addEventListener('keydown', onKeyDown);
      keyboardInset.start();
      loop.start();
      // Paint and publish once immediately, so the boot card is on screen while the store opens.
      render();
      ready = bootUp().then(render);
    },
    stop(): void {
      document.removeEventListener('keydown', onKeyDown);
      keyboardInset.stop();
      loop.stop();
    },
    startRun,
    endRun,
    toEntry,
    reset,
    skipDemo,
    machine,
  };
}

/**
 * The presentation scaffolding for one item: the carrier sentence, the conjugation slot, the rule.
 *
 * Looked up from the corpus by id rather than read off the persisted record, because a record is
 * written once at first launch and never rebuilt from a file — its `memory` is the learner's history.
 * Copying the carrier sentence into it would freeze a typo in place for the one person who has the
 * item. An item with no seed entry (a candidate minted from a conversation log, DoD-11) simply has no
 * extras, and every mode is required to cope with that. See DECISIONS D-112.
 */
function extrasFor(item: ItemRecord): ItemExtras {
  const seed = seedItem(item.id);
  if (!seed) return {};
  const extras: ItemExtras = {};
  if (seed.example) extras.example = seed.example;
  if (seed.slot) extras.slot = seed.slot;
  if (seed.derivation) extras.derivation = seed.derivation;
  if (seed.rule) {
    extras.rule = seed.rule;
    /*
     * The rule, flattened to the line the card shows (T52). Resolved here rather than in the mode
     * for the same reason the carrier sentence is: the corpus is content, and a mode that could read
     * the whole rule could read its other examples and start choosing what to ask.
     *
     * A tier-4 item names either a conversion rule or a constructive pattern; both are a headline
     * over an English prompt, and the card does not care which it got.
     */
    const rule = conversionRule(seed.rule);
    const pattern = rule ? undefined : conversionPattern(seed.rule);
    const headline = rule?.name ?? pattern?.shape;
    if (headline) {
      extras.conversion = {
        rule: headline,
        gender: rule?.gender ?? null,
        ...(seed.hazard ? { hazard: seed.hazard } : {}),
      };
    }
  }
  return extras;
}

/** The few figures a card needs that are not on the published projection. */
interface View {
  newItemsAvailable: number;
  /** Read, never written, by the card. Editing goes through `Actions.editSetup`. */
  setupDraft: Readonly<Record<BaselineField, string>>;
  setupError: string | null;
  /** The draw on screen, if any. */
  draw: RoundDraw | null;
  /** What that draw's mode decided to show. Null between items. */
  presentation: Presentation | null;
  answer: string;
  /** Exactly what was submitted for `outcome`. Shown back on the feedback card. */
  answered: string;
  /** The item being introduced, or null when nothing is (T59c). */
  teaching: TeachingView | null;
  /** El Tablero's figures, built only while it is open. Null everywhere else. */
  tracker: TrackerFigures | null;
  /** The drilldown is showing. */
  trackerDetail: boolean;
  /** The page of items awaiting a human, while the review screen is open (T76). */
  vetting: VettingPage | null;
  /** What is being typed into the log screen. Read, never written, by the card. */
  logDraft: Readonly<LogDraft>;
  /** One line: why a submission was refused, or what the last one produced. */
  logMessage: string | null;
  /** Files produced by the last export, for its receipt. */
  exportedCount: number;
  /** The one line about a wrong-variety voice, or null. */
  voiceNotice: string | null;
  /** This device can say something aloud. */
  canHear: boolean;
  /** The microphone is open on this card. */
  listening: boolean;
  /** Why the last utterance produced nothing. Never banked — see `speechTrouble` in the app. */
  speechTrouble: string | null;
  /** The end-of-run reveal, built when the ronda closed. Null outside the reveal card. */
  reveal: RevealView | null;
  /** The scaffold has been revealed on this card, so the attempt is already hinted. */
  hinted: boolean;
  /** What is typed into the encoding card. */
  encoding: string;
  /** Tiles placed so far, in placement order. Empty for a typed card. */
  placed: Placement;
  /** Where focus belongs after a tile rebuild. Null on a fresh draw. */
  tileFocus: string | null;
  /** The banked outcome the feedback card is reporting. */
  outcome: DrawOutcome | null;
  /** The one-off conversion demonstration, or null outside it. */
  demo: DemostracionView | null;
  /** What is typed into the demonstration field. Outside the card key, as every draft is. */
  demoDraft: string;
}

interface Actions {
  startRun(): void;
  /** Submit the demonstration's answer. Graded, and banked nowhere (D-252). */
  submitDemo(): void;
  /** Show the word without answering. Offered outright: there is nothing here to protect. */
  revealDemo(): void;
  /** Leave the demonstration and begin the run it delayed. */
  finishDemo(): void;
  /** What is typed into the demonstration field. */
  editDemo(value: string): void;
  /** The mounted demonstration field, so the accent bar has somewhere to type. */
  demoField(): HTMLInputElement | null;
  endRun(): void;
  toEntry(): void;
  continueRun(): void;
  editSetup(field: BaselineField, value: string): void;
  submitSetup(): void;
  editAnswer(value: string): void;
  /** The mounted answer field, for the accent bar to type into. Null when no run card is up. */
  answerField(): HTMLInputElement | null;
  /** The mounted encoding field. Spanish is typed here too, so the bar comes along. */
  encodingField(): HTMLInputElement | null;
  submitAnswer(): void;
  /** "No sé" — submit nothing, whatever is in the box. */
  submitUnknown(): void;
  /** Show the help this card offers. Marks the attempt hinted, irreversibly (invariant 2). */
  revealScaffold(): void;
  /** Leave the first-exposure card and be asked for the item. Unscored. */
  finishTeaching(): void;
  openTracker(): void;
  closeTracker(): void;
  /** Accept the four-week review's intake recommendation. Explicit, never automatic (DoD-10). */
  applyIntakeAdvice(): void;
  dismissReview(): void;
  /** One tap: the whole log as JSON and CSV (DoD-13). */
  exportLog(): void;
  openLog(): void;
  closeLog(): void;
  chooseComponent(component: ManualComponent): void;
  editLogMinutes(value: string): void;
  /** One tap on a minute chip. The common case, and the reason this is under ten seconds. */
  setLogMinutes(value: number): void;
  editLogNote(value: string): void;
  submitLog(): void;
  openVetting(): void;
  closeVetting(): void;
  /** Mark every item on this page as human-checked (T76). */
  approveVettingPage(): void;
  /** Leave this page unvetted and read the next. */
  skipVettingPage(): void;
  /** Show or hide everything that is not one of the three numbers (SPEC §8.2). */
  toggleTrackerDetail(): void;
  /** Say it aloud (T64). Silent and harmless where the device has no voice. */
  hear(text: string): void;
  /** Open the microphone. The transcript is submitted the moment it arrives (T61). */
  listen(): void;
  /** Close it and take what was heard. */
  stopListening(): void;
  editEncoding(value: string): void;
  /** Save the self-reference line and move on. No-op while the box is empty (invariant 5). */
  submitEncoding(): void;
  /** Move a tile from the bank into the assembled line. */
  placeTile(index: number): void;
  /** Take one tile back out of the line, from anywhere in it. */
  removeTile(index: number): void;
  /** Take back the last tile placed. Undo, never reset. */
  undoTile(): void;
}

/**
 * Run chrome, and there is none.
 *
 * This drew a ticking countdown until T80. Reported from a real session as "completely pointless",
 * and on inspection it was worse than pointless: nothing in this game rewards speed — no score, no
 * streak, no penalty — so a clock on the card was pressure with nothing on the other side of it,
 * applied to a retrieval, which is the one activity where hurrying costs the learner something
 * real (RESEARCH §1). See D-263.
 *
 * Kept as a function rather than deleted because the shell's chrome region is a real seam and the
 * next thing to live in it should not have to reinvent one.
 */
function chromeFor(_s: PublicGameState): ChromeSpec | null {
  return null;
}

/**
 * Build the card for the current phase.
 *
 * Cards are data: this returns a `CardSpec` and the shell owns mounting, ordering, focus, and the
 * thumb zone. `key` is what stops the 60Hz loop rebuilding the DOM — it changes only when something
 * the learner can see changes, and the timer is displayed to whole seconds, so a run repaints once
 * a second rather than sixty times.
 */
function cardFor(
  s: PublicGameState,
  caps: PlatformCapabilities,
  view: View,
  actions: Actions,
): CardSpec {
  /*
   * One line, never a modal, never blocking (SPEC §3).
   *
   * Standalone first: it is the one the learner can act on, in one action, and it costs him speech
   * entirely. The voice-variety line matters less and yields to it.
   */
  const notice: Notice | null = caps.standalone
    ? {
        message: 'Speech is off in installed mode. Reopen this page in a Safari tab.',
        testId: 'standalone-notice',
      }
    : view.voiceNotice
      ? { message: view.voiceNotice, testId: 'voice-notice' }
      : null;

  switch (s.phase) {
    case 'setup':
      return setupCard(view, actions, notice);
    case 'entry':
      /*
       * Everything the entry card is allowed to say, it says here: what the game is called, and how
       * much is waiting. No score, no streak, no "day 14", no encouragement, no comparison to
       * yesterday. The queue depth is a workload, not an achievement, and it is reported honestly
       * even when it is 300 (invariant 7). The label is "Tap or press A" because the Gamepad API
       * exposes nothing until a button press, so the Ally X cannot be detected before then and the
       * card has to invite both (SPEC §3).
       */
      /*
       * Nothing due and nothing new is a finished day, not a broken one (D-273).
       *
       * The start button is withdrawn rather than left to open an empty run — which is exactly the
       * loop reported from a real session: start, instant reveal, back here, start again. A control
       * that does nothing is worse than an absent one, because the learner reasonably concludes the
       * app is broken rather than that he is done.
       *
       * What replaces it is a statement, not an apology and not a suggestion to come back later:
       * there is no notification in this game and never will be.
       */
      const idle = s.queueDepth === 0 && view.newItemsAvailable === 0;

      return {
        key: `entry|${s.queueDepth}|${view.newItemsAvailable}|${idle ? 'idle' : ''}`,
        name: 'entry',
        notice,
        announce: idle
          ? 'Nothing due and no new items today. This is what finished looks like.'
          : queueLabel(s.queueDepth, view.newItemsAvailable),
        display: [
          headline('Ronda', 'entry-title'),
          meta(dueLabel(s.queueDepth), 'entry-queue-depth'),
          meta(newLabel(view.newItemsAvailable), 'entry-new-items'),
          ...(idle
            ? [
                meta(
                  'Nothing is due and today’s new words are done. This is what finished looks ' +
                    'like — the next items arrive when the scheduler says so.',
                  'entry-idle',
                ),
              ]
            : []),
        ],
        controls: [
          ...(idle
            ? []
            : [
                button({
                  label: 'Tap or press A',
                  testId: 'entry-start',
                  onClick: actions.startRun,
                  primary: true,
                }),
              ]),
          /*
           * The one other thing reachable from the entry card, and deliberately quiet.
           *
           * El Tablero is an instrument panel, not somewhere to go before a ronda — the card that is
           * seen at 3:40 a.m. has one obvious action, and this is not it.
           */
          button({
            label: 'El Tablero',
            testId: 'entry-tracker',
            onClick: actions.openTracker,
            // The only thing left to do on a finished day, so it stops being the quiet one.
            ...(idle ? { primary: true } : { quiet: true }),
          }),
        ],
      };
    case 'tracker':
      return trackerCard(view, actions, notice);
    case 'vetting':
      return vettingCard(view, actions, notice);
    case 'log':
      return logCard(view, actions, notice);
    case 'demo':
      return view.demo
        ? demoCard(view.demo, view, actions, notice)
        : { key: 'demo|missing', name: 'demo', notice, display: [], controls: [] };
    case 'run':
      return runCard(s, caps, view, actions, notice);
    case 'reveal':
      return revealCard(s, view, actions, notice);
    default:
      return {
        key: `boot|${s.ready}`,
        name: 'boot',
        notice,
        display: [meta('Loading', 'boot-status')],
        controls: [],
      };
  }
}

/**
 * The run card: the item on screen, or the feedback for the one just banked.
 *
 * Note what is **not** in the key: the round timer, which lives in chrome, and the answer draft.
 * Either would rebuild the card while the learner is typing — once a second in the first case —
 * and a rebuild destroys the input, dismisses the iOS keyboard, and loses the half-typed word. The
 * key changes on the draw and on the sub-state, which is exactly when the card's content changes.
 *
 * The presentation here is deliberately generic — English shown, Spanish asked for. The per-mode
 * cards (cloze, tiles, conjugate, conversión, sentence) arrive at T49–T53 and replace this body;
 * what T47 fixes in place is the *order*: answer, persist, then feedback.
 */
function runCard(
  s: PublicGameState,
  caps: PlatformCapabilities,
  view: View,
  actions: Actions,
  notice: Notice | null,
): CardSpec {
  const endButton = button({
    // Not primary: it must not take focus, or Enter would end the run instead of submitting an
    // answer (SPEC §4, D-095).
    label: 'End run and bank',
    testId: 'run-end',
    onClick: actions.endRun,
    quiet: true,
  });

  if (s.runPhase === 'teaching' && view.teaching) {
    return teachingCard(view.teaching, actions, notice, endButton, view.canHear);
  }

  if (s.runPhase === 'encoding' && view.outcome) {
    return encodingCard(view, actions, notice, endButton);
  }

  if (s.runPhase === 'feedback' && view.outcome) {
    const { attempt } = view.outcome;
    /*
     * Feedback names the error, always shows the correctly accented form, and flags a missing accent
     * on its own line (T56, BRIEF layer 2). What the card says is decided by `feedbackView`, which is
     * pure and has a fixture test — the wording *is* the requirement here, and a template inlined in
     * this function would be a requirement no test can reach.
     *
     * By the time this card exists the attempt is already on disk (DoD-5).
     */
    const shown = feedbackView({
      grade: view.outcome.grade,
      record: view.outcome.record,
      answer: view.answered,
    });
    return {
      key: `run|feedback|${attempt.id}`,
      name: 'feedback',
      notice,
      announce: [shown.errorHeading, shown.message, shown.accentNote].filter(Boolean).join(' '),
      display: [
        meta(shown.verdict, 'feedback-verdict'),
        // The error class as a *heading*: two or three words, scannable in the dark with one thumb.
        ...(shown.errorHeading ? [headline(shown.errorHeading, 'feedback-error')] : []),
        prompt(shown.correctForm, 'feedback-answer'),
        // What he actually produced, beside what it should have been. Absent when it would only
        // echo the correct form back at him.
        ...(shown.yours ? [meta(`You wrote: ${shown.yours}`, 'feedback-yours')] : []),
        ...(shown.message ? [meta(shown.message, 'feedback-message')] : []),
        // On its own line, and on an answer that *scored as correct* — the only notice the learner
        // ever gets that his spelling was not the spelling (BRIEF layer 2).
        ...(shown.accentNote ? [meta(shown.accentNote, 'feedback-accent')] : []),
        /*
         * Which sounds differed, on a spoken attempt (T61, RESEARCH §5).
         *
         * Segmental only, and never a verdict on the pronunciation: the evidence supports explicit
         * per-sound correction and does not support any claim about rhythm, stress or fluency. The
         * notes explain a result; they never soften one.
         */
        ...segmentalNotesFor(view).map((note, index) =>
          meta(`${note.letters} — ${note.message}`, `feedback-sound-${index}`),
        ),
        /*
         * What he wrote about this item, the last time it beat him (T55, invariant 5). Shown only on
         * a failure: the point of a personal association is that it is *retrieved* when the item is
         * hard, and putting it on every correct card too would make it wallpaper.
         */
        ...(shown.encoding ? [meta(shown.encoding, 'feedback-encoding')] : []),
      ],
      controls: [
        row([
          endButton,
          button({
            label: 'Next',
            testId: 'feedback-next',
            onClick: actions.continueRun,
            primary: true,
          }),
        ]),
      ],
    };
  }

  const draw = view.draw;
  const shown = view.presentation;
  if (!draw || !shown) {
    // The plan is spent. The run idles out its timer rather than ending: nothing about an empty
    // queue is a failure, and the timer owns when a run is over (invariant 7).
    return {
      key: 'run|idle',
      name: 'run',
      notice,
      display: [meta('Nothing left in the queue for this run.', 'run-status')],
      controls: [endButton],
    };
  }

  /*
   * "No sé" first, submit last: the primary action belongs bottom-right under the thumb, and
   * fail-fast is a legitimate answer rather than a penalty (SPEC §4, BRIEF gamepad X). "No sé"
   * discards whatever is half-built — "I don't know" is a claim about the item, not about the
   * characters currently in the box or the tiles currently in the line.
   */
  /*
   * The reveal, when this card has help to give and the run's escalation curve offered it (SPEC
   * §6.1.3). Absent once taken — there is nothing left to ask for — and absent entirely on a
   * free-typed sentence, whose scaffold is null by construction (D-132).
   *
   * Quiet, and to the left of the submit row, because it is a real cost and not a convenience: it
   * must not sit under the thumb that is reaching for Submit.
   */
  const revealRow =
    draw.scaffoldOffered && shown.scaffold && !view.hinted
      ? [
          button({
            label: 'Reveal a hint',
            testId: 'answer-reveal',
            onClick: actions.revealScaffold,
            quiet: true,
          }),
        ]
      : [];

  /*
   * A speech card with no microphone is a typed card (T60, DoD-4).
   *
   * Silently: the learner did not choose the platform and cannot change it, and a notice apologising
   * for it on every card would be noise. The item is still due and still production, so it is still
   * asked — just with a keyboard.
   */
  const speechAvailable = caps.speechAvailable;

  const submitRow = row([
    button({ label: 'No sé', testId: 'answer-unknown', onClick: actions.submitUnknown }),
    button({
      label: 'Submit',
      testId: 'answer-submit',
      onClick: actions.submitAnswer,
      primary: true,
    }),
  ]);

  return {
    // The mode is in the key as well as the index: two consecutive draws of the same item in one
    // run are different cards when the mode differs, and a stale card would ask the old question.
    // The placement is in it too, because a tile card's controls *are* its state — unlike a typed
    // answer, where a rebuild would destroy the field and dismiss the keyboard mid-word.
    key:
      `run|item|${draw.index}|${shown.mode}|${view.hinted ? 'hint' : ''}` +
      `|${view.listening ? 'mic' : ''}|${view.speechTrouble ?? ''}|${view.placed.join('-')}`,
    name: 'run',
    tier: draw.tier,
    notice,
    announce: shown.announce,
    // Only ever set by a tile action. Every other card focuses its primary action as usual.
    ...(view.tileFocus ? { focus: view.tileFocus } : {}),
    display: [
      // The tier, named and coloured. The name is what carries it; the colour is decoration that
      // stops a ninety-second ronda from looking like one long card (D-185).
      /*
       * The tier, and — where it applies — the unvetted marker (T76, the Diana gate).
       *
       * Small, and on the same line as the tier so it costs no vertical space. It is a statement
       * about the *item*, not a warning: the Spanish may be right, it simply has not been read by a
       * human yet, and the learner is entitled to know which of the two he is looking at.
       */
      tierLine(draw.tier, draw.item.vetted),
      meta(shown.instruction, 'run-instruction'),
      promptSegments(shown.segments, 'run-prompt'),
      // The English that makes the prompt answerable. Absent where the prompt is already English,
      // rather than rendered empty — a blank line under the prompt reads as something missing.
      ...(shown.gloss ? [meta(shown.gloss, 'run-gloss')] : []),
      // Read-only, in the display region, once it has been asked for. It stays on screen until the
      // answer is submitted: a hint that vanished would have been paid for and not received.
      ...(view.hinted && shown.scaffold ? [meta(shown.scaffold, 'run-scaffold')] : []),
      // Why the microphone produced nothing. Not a wrong answer, and nothing was banked for it.
      ...(view.speechTrouble ? [meta(view.speechTrouble, 'speech-trouble')] : []),
    ],
    controls:
      shown.input === 'speech' && speechAvailable
        ? [...speechControls(view, actions, shown), ...revealRow, submitRow, endButton]
        : shown.input === 'tiles' && shown.tiles
        ? [...tileControls(shown.tiles, view.placed, actions), ...revealRow, submitRow, endButton]
        : [
            textField({
              label: shown.fieldLabel,
              testId: 'answer-input',
              value: view.answer,
              onInput: actions.editAnswer,
            }),
            /*
             * Below the field and above everything else, which is where an input accessory belongs:
             * with the software keyboard up it pins itself directly over the keyboard's top edge
             * (SPEC §4), and with the keyboard down it is simply the next row of the card. iOS
             * long-press is not relied upon — mistyping `ñ` is an interface failure and this game
             * does not score those.
             */
            accentBar({ field: actions.answerField, onInsert: actions.editAnswer }),
            ...revealRow,
            submitRow,
            endButton,
          ],
  };
}

/**
 * The per-sound notes for the attempt the feedback card is about, if it was a spoken one.
 *
 * Empty for a typed attempt, and empty when the transcript matched: a correct answer that came with
 * a list of corrections would read as one that was not quite correct.
 */
function segmentalNotesFor(view: View): SegmentalNote[] {
  if (view.outcome?.attempt.mode !== 'speech' || view.outcome.grade.correct) return [];
  return segmentalFeedback(view.outcome.grade.correctForm, view.answered);
}

/**
 * Push-to-talk, and a way past it (T61).
 *
 * The listening button is the primary target — it is what the card is for — and it is joined by a
 * typed field, always. Speech recognition fails for reasons that have nothing to do with Spanish: a
 * sleeping baby in the room, a bus, a permission dialog dismissed weeks ago. A card that could only
 * be answered by voice would turn any of those into a lost review (invariant 7).
 */
function speechControls(view: View, actions: Actions, shown: Presentation): HTMLElement[] {
  /*
   * "Hear it" is offered **only where the answer is already on screen** — which is tier 1, where the
   * Spanish is the prompt and the task is the sound (D-198). On every other speech card the Spanish
   * *is* the answer, so playing it would hand it over, and a control that hands over the answer has
   * to cost the interval (invariant 2). Rather than build a second hinting path, the button simply
   * is not there; the scaffold reveal already covers that need.
   */
  const model =
    view.canHear && shown.gloss !== null
      ? [
          button({
            label: 'Hear it',
            testId: 'speech-hear',
            onClick: () => actions.hear(shown.expected),
            quiet: true,
          }),
        ]
      : [];

  return [
    ...model,
    button({
      /*
       * "Tap to speak", not "Hold to speak".
       *
       * The control has always been a toggle — tap to open the microphone, tap again to close it —
       * and the label described a press-and-hold that does not exist. Reported from a real device
       * as the microphone opening and nothing registering, which is exactly what someone does when
       * they hold a button, speak, release, and wait for a release that means nothing.
       *
       * The label follows the control rather than the control following the label, because
       * push-to-talk on a touchscreen means a finger covering the card while the answer is spoken.
       */
      label: view.listening ? 'Listening — tap when done' : 'Tap to speak',
      testId: 'speech-listen',
      onClick: view.listening ? actions.stopListening : actions.listen,
      primary: true,
    }),
    textField({
      label: 'Or type it',
      testId: 'answer-input',
      value: view.answer,
      onInput: actions.editAnswer,
    }),
    accentBar({ field: actions.answerField, onInsert: actions.editAnswer }),
  ];
}

/**
 * The assembled line, the bank, and undo (T50).
 *
 * The line comes first because it is what is being built and it has to be readable without looking
 * for it; the bank sits under it, nearest the thumb, because that is what gets tapped repeatedly.
 * Every tile in both is a real button — reachable by thumb, by Tab, and by the D-pad that T77 maps
 * onto the same arrow keys.
 */
function tileControls(
  tiles: readonly string[],
  placed: Placement,
  actions: Actions,
): HTMLElement[] {
  return [
    tileGroup({
      tiles,
      show: placed,
      testId: 'tile-line',
      label: 'Your sentence',
      // Tapping a placed tile takes it back out, from anywhere in the line — not only the end.
      onActivate: actions.removeTile,
      emptyLabel: 'Tap the words below, in order.',
    }),
    tileGroup({
      tiles,
      show: remaining(tiles, placed),
      testId: 'tile-bank',
      label: 'Words',
      onActivate: actions.placeTile,
      emptyLabel: 'All words placed.',
    }),
    button({
      label: 'Undo',
      testId: 'tile-undo',
      onClick: actions.undoTile,
      quiet: true,
      disabled: placed.length === 0,
    }),
  ];
}

/**
 * The end-of-run reveal (T57, SPEC §1).
 *
 * The game's only reward surface, and the constraint on it is severe: **nothing here may be
 * maximisable**. Every figure is a description of the schedule — which items moved and when they come
 * back — and not a measure of how the run went. No score, no total, no best, no comparison with the
 * last ronda.
 *
 * That is why each section is a *list of words* rather than a count. "Six promoted" is a number to
 * beat; "aquí, cobija, dormir" is a statement about three particular words. It is also why an empty
 * run says so plainly: a ronda that promoted nothing is an ordinary outcome and not a failure, and
 * the card that reports it must not apologise for it.
 */
function revealCard(
  s: PublicGameState,
  view: View,
  actions: Actions,
  notice: Notice | null,
): CardSpec {
  const shown = view.reveal;
  const sections: HTMLElement[] = [];

  if (shown && !shown.empty) {
    // Promoted first: the longer interval is the thing the ronda actually bought.
    pushSection(sections, 'reveal-promoted', 'Further away now', shown.promoted);
    pushSection(sections, 'reveal-returning', 'Back soon', shown.returning);
    pushSection(sections, 'reveal-new', 'New today', shown.introduced);

    for (const rule of shown.rules) {
      const node = el('div', 'reveal__rule');
      node.dataset['testid'] = `reveal-rule-${rule.id}`;
      node.append(
        text('p', 'reveal__section-title', rule.rule, `reveal-rule-${rule.id}-name`),
        text('p', 'meta', rule.reach, `reveal-rule-${rule.id}-reach`),
      );
      sections.push(node);
    }
  }

  return {
    key: `reveal|${s.runSummary?.runId ?? ''}`,
    name: 'reveal',
    notice,
    announce: 'Ronda ended.',
    display: [
      headline('Ronda ended', 'reveal-title'),
      ...(shown && !shown.empty
        ? sections
        : // Stated, not softened, and not congratulated. An empty ronda is a fact about the
          // schedule; the queue was short or the timer was.
          [meta('Nothing moved this time.', 'reveal-empty')]),
    ],
    controls: [
      button({
        label: 'Continue',
        testId: 'reveal-continue',
        onClick: actions.toEntry,
        primary: true,
      }),
    ],
  };
}

/** One titled group of items. Omitted entirely when it has nothing in it — never rendered as zero. */
function pushSection(
  into: HTMLElement[],
  testId: string,
  title: string,
  lines: readonly { id: string; es: string; en: string; detail: string }[],
): void {
  if (lines.length === 0) return;
  const section = el('section', 'reveal__section');
  section.dataset['testid'] = testId;
  section.append(text('h2', 'reveal__section-title', title, `${testId}-title`));

  const list = el('ul', 'reveal__list');
  for (const line of lines) {
    const entry = el('li', 'reveal__item');
    entry.dataset['testid'] = `${testId}-item`;
    entry.dataset['item'] = line.id;
    const word = text('span', 'reveal__es', line.es);
    word.lang = 'es';
    entry.append(word, text('span', 'reveal__en', line.en));
    if (line.detail) entry.append(text('span', 'reveal__detail', line.detail));
    list.append(entry);
  }
  section.append(list);
  into.push(section);
}

/**
 * El Tablero (T66, SPEC §8).
 *
 * Three numbers, and everything else behind one tap. No celebration, no encouragement, no emoji, no
 * colour-coded judgement of the person, and no comparison — not with other learners, not with
 * published averages, not with his own best week.
 *
 * Every figure renders `data-window-days`, which is what DoD-8 walks: a number without the window it
 * was drawn over is a number that cannot be checked, and per-day granularity is forbidden outright.
 */
function trackerCard(view: View, actions: Actions, notice: Notice | null): CardSpec {
  const shown = view.tracker;
  const figures = shown ? (view.trackerDetail ? shown.detail : shown.summary) : [];

  const display: HTMLElement[] = [
    headline('El Tablero', 'tracker-title'),
    ...figures.map(figureNode),
  ];

  if (view.trackerDetail && shown) {
    // What the game cannot see for itself. "Not logged" is never rendered as zero (DoD-9).
    display.push(text('h2', 'reveal__section-title', 'Logged by hand', 'tracker-manual-title'));
    for (const component of shown.components) {
      const node = meta(
        `${MANUAL_LABEL[component.component]} — ${
          component.notLogged ? 'not logged' : `${Math.round(component.minutes ?? 0)} min`
        }`,
        `tracker-component-${component.component}`,
      );
      node.dataset['windowDays'] = String(DEFAULT_WINDOW_DAYS);
      display.push(node);
    }

    display.push(text('h2', 'reveal__section-title', 'Before starting', 'tracker-baseline-title'));
    for (const line of shown.baseline) {
      display.push(meta(`${line.label} — ${line.value}`, 'tracker-baseline'));
    }

    /*
     * Weeks 1-12 (T73). A list, not a chart with a line through it: no trend, no projection, no
     * interpretation. Every point carries its own week, and a week not yet reached is simply absent.
     */
    display.push(text('h2', 'reveal__section-title', 'By week', 'tracker-weeks-title'));
    for (const point of shown.weeks) {
      const node = meta(
        `Week ${point.week} — ${formatWeekPoint(point)}`,
        `tracker-week-${point.week}`,
      );
      // A week is seven days, which satisfies the floor exactly (DoD-8).
      node.dataset['windowDays'] = '7';
      display.push(node);
    }

    if (shown.verdict.available) {
      display.push(text('h2', 'reveal__section-title', 'Week 6', 'tracker-verdict-title'));
      for (const criterion of shown.verdict.criteria) {
        display.push(
          meta(
            `${criterion.label} — ${criterion.value} against ${criterion.against}`,
            'tracker-criterion',
          ),
        );
      }
      // The sentence, unsoftened, with no remedy beside it (SPEC §8.7).
      if (shown.verdict.statement) {
        display.push(prompt(shown.verdict.statement, 'tracker-verdict'));
      }
    }

    // The checkpoint calendar and the three diagnoses, in the order the brief fixes (T74).
    display.push(text('h2', 'reveal__section-title', 'Checkpoints', 'tracker-checkpoints-title'));
    for (const checkpoint of shown.checkpoints) {
      display.push(
        meta(`${checkpoint.label} — ${checkpoint.dueIso}`, 'tracker-checkpoint'),
      );
    }
    if (shown.checkpoints.some((checkpoint) => checkpoint.passed)) {
      for (const diagnosis of shown.diagnoses) {
        display.push(
          meta(
            `${diagnosis.order}. ${diagnosis.statement} ${
              diagnosis.supported ? 'The data supports this.' : 'The data does not support this.'
            } (${diagnosis.evidence})`,
            `tracker-diagnosis-${diagnosis.key}`,
          ),
        );
      }
    }
  }

  if (shown?.review) {
    // Once a month, and only when one is due. The offer is a question, and applying it is a
    // separate, explicit tap (DoD-10).
    display.push(text('h2', 'reveal__section-title', 'Four weeks', 'tracker-review-title'));
    for (const line of shown.review.lines) {
      const node = meta(
        `${line.label} — ${line.value}${line.note ? ` · ${line.note}` : ''}`,
        `tracker-review-${line.label.toLowerCase().replace(/[^a-z]+/g, '-')}`,
      );
      node.dataset['windowDays'] = String(shown.review.windowDays);
      display.push(node);
    }
    if (shown.review.offer) display.push(prompt(shown.review.offer, 'tracker-review-offer'));
  }

  return {
    /*
     * The review's state is in the key as well as the figures.
     *
     * Without it, accepting the recommendation wrote the new cap to disk and left the offer on
     * screen: the three summary numbers had not changed, so the shell saw the same key and never
     * rebuilt. A card whose content can change without its key changing is a card that lies.
     */
    key:
      `tracker|${view.trackerDetail}|${shown?.summary.map((f) => f.value).join('|') ?? ''}` +
      `|${shown?.review?.offer ?? ''}|${shown?.review ? 'due' : ''}|${view.exportedCount}`,
    name: 'tracker',
    notice,
    announce: 'El Tablero.',
    display,
    controls: [
      button({
        label: view.trackerDetail ? 'Show the three numbers' : 'Everything else',
        testId: 'tracker-detail',
        onClick: actions.toggleTrackerDetail,
        quiet: true,
      }),
      /*
       * The way to Diana's review screen (T76), and the only place it is reachable from.
       *
       * The count is on the button because it is the whole reason to tap it, and because an
       * unvetted corpus that says nothing about itself is one nobody ever reviews.
       */
      /*
       * The review's offer, when one is being made (DoD-10). Explicit, and it is the only thing that
       * ever changes the intake cap — the app does not retune itself.
       */
      ...(shown?.review?.offer
        ? [
            button({
              label: `Change to ${shown.review.proposedIntakeCap} a day`,
              testId: 'tracker-review-apply',
              onClick: actions.applyIntakeAdvice,
              quiet: true,
            }),
            button({
              label: 'Leave it as it is',
              testId: 'tracker-review-dismiss',
              onClick: actions.dismissReview,
              quiet: true,
            }),
          ]
        : shown?.review
          ? [
              button({
                label: 'Read',
                testId: 'tracker-review-dismiss',
                onClick: actions.dismissReview,
                quiet: true,
              }),
            ]
          : []),
      // One tap, and four files (DoD-13).
      button({
        label: view.exportedCount > 0 ? `${view.exportedCount} files saved` : 'Export everything',
        testId: 'tracker-export',
        onClick: actions.exportLog,
        quiet: true,
      }),
      // One tap from the tracker (SPEC §8.4). Nothing prompts him to use it.
      button({
        label: 'Log time',
        testId: 'tracker-log',
        onClick: actions.openLog,
        quiet: true,
      }),
      button({
        label:
          shown && shown.unvetted > 0
            ? `Review Spanish (${shown.unvetted} unchecked)`
            : 'Review Spanish',
        testId: 'tracker-vetting',
        onClick: actions.openVetting,
        quiet: true,
      }),
      button({
        label: 'Back',
        testId: 'tracker-close',
        onClick: actions.closeTracker,
        primary: true,
      }),
    ],
  };
}

/**
 * The manual log (T68, T69, SPEC §8.4).
 *
 * Four components, one required field, and a receipt. The whole screen is built around a number:
 * **under ten seconds**, one-handed — pick a component, tap a minute chip, save. Typing an exact
 * figure is still there for the times it matters; the chips exist so the common case is not a
 * keyboard.
 *
 * Nothing here nags. There is no reminder, no "you haven't logged in N days", and no count of
 * anything — a tracker that asked would become a thing to feel guilty about, which is the failure
 * mode this whole app is shaped around.
 */
function logCard(view: View, actions: Actions, notice: Notice | null): CardSpec {
  const draft = view.logDraft;
  const wantsNote = takesNote(draft.component);

  const components = MANUAL_COMPONENTS.map((component) =>
    button({
      label: COMPONENT_LABELS[component],
      testId: `log-component-${component}`,
      onClick: () => actions.chooseComponent(component),
      // The chosen one is the primary, so the card always has exactly one and focus lands sensibly.
      primary: component === draft.component,
    }),
  );

  const chips = el('div', 'accent-bar');
  chips.dataset['testid'] = 'log-minutes-chips';
  chips.setAttribute('role', 'group');
  chips.setAttribute('aria-label', 'Minutes');
  for (const minutes of MINUTE_CHIPS) {
    const chip = button({
      label: String(minutes),
      testId: `log-minutes-${minutes}`,
      ariaLabel: `${minutes} minutes`,
      onClick: () => actions.setLogMinutes(minutes),
    });
    chip.classList.add('accent-bar__key');
    chips.append(chip);
  }

  return {
    // The drafts are not in the key: rebuilding on every keystroke would dismiss the iOS keyboard.
    key: `log|${draft.component}|${view.logMessage ?? ''}`,
    name: 'log',
    notice,
    announce: 'Log time.',
    display: [
      headline('Log time', 'log-title'),
      meta(COMPONENT_LABELS[draft.component], 'log-chosen'),
      ...(wantsNote
        ? [
            meta(
              'Repeat errors, one per line, as español = english. They become items you will be asked.',
              'log-note-hint',
            ),
          ]
        : []),
      // A receipt or a refusal. One line, and only after something happened.
      ...(view.logMessage ? [meta(view.logMessage, 'log-message')] : []),
    ],
    controls: [
      ...components,
      chips,
      numberField({
        label: 'Minutes',
        testId: 'log-minutes',
        value: draft.minutes,
        placeholder: 'or type it',
        min: 1,
        onInput: actions.editLogMinutes,
      }),
      ...(wantsNote
        ? [
            textArea({
              label: 'Repeat errors',
              testId: 'log-note',
              value: draft.note,
              placeholder: 'la cuenta = the bill',
              onInput: actions.editLogNote,
              rows: 3,
            }),
          ]
        : []),
      row([
        button({ label: 'Back', testId: 'log-close', onClick: actions.closeLog, quiet: true }),
        button({ label: 'Save', testId: 'log-save', onClick: actions.submitLog, primary: true }),
      ]),
    ],
  };
}

/**
 * The Diana gate (T76).
 *
 * Six items at a time, each with its Spanish, its English and the sentence it lives in — the minimum
 * needed to *judge* a line rather than rubber-stamp it. The machine's verdict is shown as context
 * and pre-ticks nothing: machine vetting is a filter, not authority, and the whole point of this
 * screen is that a human looked.
 *
 * There is no reject. An item she is unsure about is skipped and comes back; the absence of a
 * human's confirmation is the safe state, so skipping costs nothing and deletes nothing.
 */
function vettingCard(view: View, actions: Actions, notice: Notice | null): CardSpec {
  const page = view.vetting;
  const done = !page || page.rows.length === 0;

  const rows: HTMLElement[] = done
    ? [meta('Everything has been checked.', 'vetting-done')]
    : page.rows.map((row) => {
        const node = el('div', 'vetting__row');
        node.dataset['testid'] = 'vetting-row';
        node.dataset['item'] = row.id;

        const spanish = text('p', 'vetting__es', row.es, undefined);
        spanish.lang = 'es';
        node.append(spanish, text('p', 'vetting__en', row.en, undefined));
        if (row.example) {
          const example = text('p', 'vetting__example', row.example, undefined);
          example.lang = 'es';
          node.append(example);
        }
        // Context, never a recommendation.
        if (row.machineVerdict) {
          node.append(text('p', 'vetting__verdict', row.machineVerdict, undefined));
        }
        return node;
      });

  return {
    key: `vetting|${page?.pageIndex ?? 0}|${page?.remaining ?? 0}`,
    name: 'vetting',
    notice,
    announce: page ? vettingProgress(page) : 'Everything has been checked.',
    display: [
      headline('Review Spanish', 'vetting-title'),
      // A workload, stated plainly. Not a progress bar, and never a "well done" — this is somebody
      // doing the reviewer a favour.
      meta(page ? vettingProgress(page) : '', 'vetting-progress'),
      ...rows,
    ],
    controls: done
      ? [button({ label: 'Back', testId: 'vetting-close', onClick: actions.closeVetting, primary: true })]
      : [
          button({
            label: 'Skip these',
            testId: 'vetting-skip',
            onClick: actions.skipVettingPage,
            quiet: true,
          }),
          button({
            label: 'These are right',
            testId: 'vetting-approve',
            onClick: actions.approveVettingPage,
            primary: true,
          }),
          button({
            label: 'Back',
            testId: 'vetting-close',
            onClick: actions.closeVetting,
            quiet: true,
          }),
        ],
  };
}

/** One week of the novelty panel, in words. Nulls are said, never drawn as zero. */
function formatWeekPoint(point: WeekPoint): string {
  const retention = point.retention === null ? 'no reviews' : `${Math.round(point.retention * 100)}%`;
  const minutes = point.medianMinutes === null ? 'no sessions' : `${Math.round(point.medianMinutes)} min`;
  const sessions = point.sessions === null ? '0 sessions' : `${point.sessions} sessions`;
  return `${retention} · ${minutes} · ${sessions}`;
}

/** One figure, with the window it was drawn over attached to the element (DoD-8). */
function figureNode(spec: Figure): HTMLElement {
  const node = el('div', 'figure');
  node.dataset['testid'] = `figure-${spec.label.toLowerCase().replace(/[^a-z]+/g, '-')}`;
  // The assertion DoD-8 walks. Every rendered window, everywhere, ≥ 7.
  node.dataset['windowDays'] = String(spec.windowDays);
  if (spec.flag !== 'none') node.dataset['flag'] = spec.flag;

  node.append(
    text('p', 'figure__label', spec.label, undefined),
    text('p', 'figure__value', spec.value, undefined),
    text('p', 'figure__window', `${spec.windowDays} days`, undefined),
  );
  if (spec.note) node.append(text('p', 'figure__note', spec.note, undefined));
  return node;
}

/** The tier's name, with the unvetted marker beside it where one is owed (T76). */
function tierLine(tier: number, vetted: boolean): HTMLElement {
  const node = text('p', 'tier-mark', TIER_NAME[tier] ?? '', 'run-tier');
  if (!vetted) {
    const mark = text('span', 'unvetted', '·  unchecked', 'run-unvetted');
    // Named for a screen reader, because "·" is not a word and the fact matters.
    mark.setAttribute('aria-label', 'not yet checked by a human');
    node.append(mark);
  }
  return node;
}

const MANUAL_LABEL: Readonly<Record<string, string>> = {
  course: 'Course study',
  input: 'Listening and reading',
  conversation: 'Conversation with Diana',
  narration: 'Narrating out loud',
};

/**
 * The first-exposure card (T59c).
 *
 * The one card in the game whose job is to *show* rather than to ask. Everything the corpus knows
 * about the item is on it — the carrier sentence, the slot, the rule — because every other card is
 * careful to withhold exactly this, and he is about to meet one of those in a few seconds.
 *
 * One control, and it is not labelled "continue": "Ask me" names what actually happens next, which
 * is the whole point of the step.
 */
function teachingCard(
  shown: TeachingView,
  actions: Actions,
  notice: Notice | null,
  endButton: HTMLButtonElement,
  canHear: boolean,
): CardSpec {
  const spanish = prompt(shown.es, 'teach-es');
  spanish.lang = 'es';

  return {
    key: `run|teach|${shown.es}`,
    name: 'teaching',
    tier: shown.tier,
    notice,
    announce: shown.announce,
    display: [
      text('p', 'tier-mark', shown.label, 'teach-label'),
      spanish,
      meta(shown.en, 'teach-en'),
      ...shown.lines.map((line) => {
        const node = meta(line.text, line.testId);
        if (line.spanish) node.lang = 'es';
        return node;
      }),
    ],
    controls: [
      /*
       * Hearing it is part of meeting it (T82).
       *
       * The card is spoken automatically on arrival — the learner asked for the pronunciation to be
       * part of the introduction, not a thing to go and fetch — and these repeat it. Two buttons
       * rather than one because they sound different: the bare word gets a synthesiser's list
       * intonation, and the same word inside its sentence gets real prosody.
       *
       * Free of the hinting rule that keeps "Hear it" off every other card (D-198). That rule
       * exists because playing the answer aloud hands the answer over, and on this card the answer
       * is already on screen in letters an inch high. There is nothing here to protect.
       */
      ...(canHear
        ? [
            button({
              label: 'Hear it',
              testId: 'teach-hear',
              onClick: () => actions.hear(shown.say.word),
              quiet: true,
            }),
            ...(shown.say.sentence
              ? [
                  button({
                    label: 'In a sentence',
                    testId: 'teach-hear-sentence',
                    onClick: () => actions.hear(shown.say.sentence!),
                    quiet: true,
                  }),
                ]
              : []),
          ]
        : []),
      button({
        label: 'Ask me',
        testId: 'teach-continue',
        onClick: actions.finishTeaching,
        primary: true,
      }),
      endButton,
    ],
  };
}

/**
 * The forced self-reference encoding card (T55, invariant 5).
 *
 * Unscored, and it looks it: no verdict, no "wrong", no count of how many times this item has beaten
 * him. It is a consequence of a graded second failure, not a reprimand for one, and nothing in this
 * game turns a lapse into a loss.
 *
 * The Spanish is on screen while he writes. This is not a retrieval attempt — hiding it would only
 * make the association harder to form, and there is nothing here to cheat at.
 */
function encodingCard(
  view: View,
  actions: Actions,
  notice: Notice | null,
  endButton: HTMLButtonElement,
): CardSpec {
  const spec = encodingPrompt(view.outcome!.record);
  return {
    // The draft is not in the key: rebuilding on every keystroke would dismiss the iOS keyboard.
    key: `run|encoding|${view.outcome!.attempt.id}`,
    name: 'encoding',
    notice,
    announce: spec.announce,
    display: [
      meta(spec.instruction, 'encoding-instruction'),
      prompt(spec.es, 'encoding-target'),
      meta(spec.en, 'encoding-gloss'),
    ],
    controls: [
      textField({
        label: spec.fieldLabel,
        testId: 'encoding-input',
        value: view.encoding,
        onInput: actions.editEncoding,
      }),
      accentBar({ field: actions.encodingField, onInsert: actions.editEncoding }),
      button({
        label: 'Save and continue',
        testId: 'encoding-save',
        onClick: actions.submitEncoding,
        primary: true,
      }),
      endButton,
    ],
  };
}

/**
 * La demostración — one rule, one word, before the first ronda (D-252).
 *
 * The card is deliberately shaped like the game rather than like a tutorial: a rule at the top, a
 * prompt, a field, a submit. What makes it different is that nothing it produces is recorded.
 *
 * There is no "skip". There is a reveal, offered outright rather than held behind a cost, because
 * there is nothing here worth protecting — no interval to move, no accuracy to measure, and no tier
 * that opens sooner for getting it right.
 */
function demoCard(
  demo: DemostracionView,
  view: View,
  actions: Actions,
  notice: Notice | null,
): CardSpec {
  const answered = demo.answer !== null;

  return {
    // The draft is not in the key, for the reason the setup card's is not: rebuilding on every
    // keystroke dismisses the iOS keyboard mid-word.
    key: `demo|${answered ? 'answered' : 'asking'}|${demo.correct ?? ''}`,
    name: 'demo',
    tier: 4,
    notice,
    announce: demo.announce,
    display: [
      headline(demo.label, 'demo-title'),
      meta(demo.ruleName, 'demo-rule'),
      meta(demo.ruleNote, 'demo-rule-note'),
      meta('Now the Spanish for this word. You have not been taught it.', 'demo-instruction'),
      headline(demo.en, 'demo-prompt'),
      ...(demo.answer ? [meta(demo.answer, 'demo-answer')] : []),
      ...(demo.verdict ? [meta(demo.verdict, 'demo-verdict')] : []),
      // The claim, last, and only ever after the word has actually been produced.
      ...(demo.claim ? [meta(demo.claim, 'demo-claim')] : []),
    ],
    controls: answered
      ? [
          button({
            label: 'Start the ronda',
            testId: 'demo-continue',
            onClick: actions.finishDemo,
            primary: true,
          }),
        ]
      : [
          textField({
            label: 'The Spanish',
            testId: 'demo-input',
            value: view.demoDraft,
            onInput: actions.editDemo,
          }),
          accentBar({ field: actions.demoField, onInsert: actions.editDemo }),
          row([
            button({
              label: 'Show me',
              testId: 'demo-reveal',
              onClick: actions.revealDemo,
              quiet: true,
            }),
            button({
              label: 'Submit',
              testId: 'demo-submit',
              onClick: actions.submitDemo,
              primary: true,
            }),
          ]),
        ],
  };
}

/**
 * The first-launch setup card (T46b).
 *
 * It exists to serve the week-6 verdict, which is allowed to say the design was wrong (SPEC §8.7).
 * A verdict needs something to be measured against, and by week 6 the pre-game numbers are gone.
 *
 * Both fields are optional and blank is a real answer meaning *not known* — stored as null, never
 * as zero. That is not politeness about a form: a baseline of 0% retention or 0 minutes a day would
 * make every later comparison flattering, and the one thing this card must not do is make the
 * game's own kill criteria easier to pass.
 *
 * The card carries no progress dots, no "step 1 of 2", and no welcome. It is a question, asked once.
 */
function setupCard(view: View, actions: Actions, platformNotice: Notice | null): CardSpec {
  const retention = baselineFieldRange('retentionPct');
  const minutes = baselineFieldRange('dailyMinutes');
  // A refused field outranks the standalone-speech line: it is the one thing blocking the tap.
  const notice: Notice | null = view.setupError
    ? { message: view.setupError, testId: 'setup-error' }
    : platformNotice;

  return {
    // The draft is not in the key: rebuilding on every keystroke would dismiss the iOS keyboard.
    key: `setup|${view.setupError ?? ''}`,
    name: 'setup',
    notice,
    announce: 'Baseline. Two optional numbers, asked once.',
    display: [
      headline('Before you start', 'setup-title'),
      meta(
        'Two numbers about where you are today, so week 6 has something to be measured against.',
        'setup-why',
      ),
      meta(
        'Leave either blank if you do not know it. Blank is recorded as not known, not as zero.',
        'setup-optional',
      ),
      meta('Asked once. Editable later from El Tablero.', 'setup-once'),
    ],
    controls: [
      numberField({
        label: 'Retention now, if you know it (%)',
        testId: 'setup-retention',
        value: view.setupDraft.retentionPct,
        placeholder: 'Not known',
        min: retention.min,
        max: retention.max,
        onInput: (value) => actions.editSetup('retentionPct', value),
      }),
      numberField({
        label: 'Spanish minutes on a typical day now',
        testId: 'setup-minutes',
        value: view.setupDraft.dailyMinutes,
        placeholder: 'Not known',
        min: minutes.min,
        max: minutes.max,
        onInput: (value) => actions.editSetup('dailyMinutes', value),
      }),
      button({
        label: 'Save and continue',
        testId: 'setup-continue',
        onClick: actions.submitSetup,
        primary: true,
      }),
    ],
  };
}

/**
 * The queue depth, in words.
 *
 * Singular/plural because "1 due items" reads as a machine talking, and this card is the first
 * thing seen at 3:40 a.m. Zero is stated plainly rather than celebrated: an empty queue is a fact
 * about the schedule, not a reward, and "All caught up!" would be exactly the encouragement the
 * brief rules out.
 */
function dueLabel(depth: number): string {
  if (depth === 0) return 'Nothing due';
  return depth === 1 ? '1 item due' : `${depth} items due`;
}

/**
 * How many new items this run will introduce.
 *
 * Rendered even when it is zero, and stated as a fact rather than as a limit reached: "No new items
 * today" is what intake suppression looks like from the outside, and it is a normal, healthy state
 * that means the review queue is full enough (invariant 6). Nothing here frames it as a cap the
 * learner has hit or a thing to unlock.
 */
function newLabel(count: number): string {
  if (count === 0) return 'No new items today';
  return count === 1 ? '1 new item' : `${count} new items`;
}

function queueLabel(depth: number, newItems: number): string {
  return `${dueLabel(depth)}. ${newLabel(newItems)}.`;
}


